import { Link } from 'react-router-dom';
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import ReactECharts from "echarts-for-react";
import Select from "react-select";
import "./SurveyPage.css";

const DEFAULT_SLUG = "freemasonry-membership-engagement-retention";
const MEETING_TYPE_OPTIONS = [
    "Regular meeting",
    "Special or emergency meeting",
    "Installation",
    "Convocation",
    "Entered Apprentice degree",
    "Fellow Craft degree",
    "Master Mason degree",
    "Mark Master Mason degree",
    "Royal Arch degree",
    "Council of Management",
    "Knights Mason Council meeting",
    "Knight of the Sword degree",
    "Knight of the East degree",
    "Knight of the East and West degree",
    "Knights Templar Conclave",
    "Knights Templar degree",
    "Knights of Malta degree",
    "Committee meeting",
    "Fundraising / charity event",
    "Festive Board",
    "Lecture or education",
].map((label) => ({ value: label, label }));

const getSelectPortalTarget = () => (typeof document === "undefined" ? undefined : document.body);
const formatSurveyDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(`${dateString}T00:00:00`);
    if (Number.isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString("en-ZA");
};

const buildAttendanceSurveyTitle = ({ lodge, otherLodge, meetingDate, meetingType } = {}) => {
    const cleanLodge = lodge === "__other__" ? String(otherLodge || "").trim() : String(lodge || "").trim();
    return [cleanLodge, meetingDate, meetingType].filter(Boolean).join(" - ") || "Meeting RSVP Forecast";
};

const getMeetingTypeClass = (meetingType = "") => {
    const type = String(meetingType).toLowerCase();
    if (type.includes("installation")) return "installation";
    if (type.includes("entered apprentice")) return "entered-apprentice";
    if (type.includes("fellow craft")) return "fellow-craft";
    if (type.includes("master mason")) return "master-mason";
    if (type.includes("convocation")) return "convocation";
    if (type.includes("council")) return "council";
    if (type.includes("conclave")) return "conclave";
    if (type.includes("degree")) return "degree";
    if (type.includes("regular")) return "regular";
    return "other";
};

const isSurveyExpired = (surveyItem) => {
    if (!surveyItem?.expires_at) return false;
    return new Date(surveyItem.expires_at).getTime() < Date.now();
};

const isRsvpSurveyKind = (kind) => kind === "meeting_attendance";
const isDigitalAttendanceKind = (kind) => kind === "digital_attendance";
const isMeetingFormSurveyKind = (kind) => isRsvpSurveyKind(kind) || isDigitalAttendanceKind(kind);
const RSVP_RESPONDER_MEMORY_KEY = "connaught361:rsvp-responder-memory:v1";

const getBrowserStorage = () => {
    try {
        return typeof window !== "undefined" ? window.localStorage : null;
    } catch (error) {
        return null;
    }
};

const normalizeMemoryKeyPart = (value) => String(value || "").trim().toLowerCase().replace(/\s+/g, " ");
const getResponderMemoryKeys = ({ name, email }) => {
    const cleanEmail = normalizeMemoryKeyPart(email);
    const cleanName = normalizeMemoryKeyPart(name);
    return [
        cleanEmail ? `email:${cleanEmail}` : null,
        cleanName ? `name:${cleanName}` : null,
    ].filter(Boolean);
};

const readResponderMemoryStore = () => {
    const storage = getBrowserStorage();
    if (!storage) return { lastKey: "", profiles: {} };
    try {
        const parsed = JSON.parse(storage.getItem(RSVP_RESPONDER_MEMORY_KEY) || "{}");
        return {
            lastKey: String(parsed.lastKey || ""),
            profiles: parsed.profiles && typeof parsed.profiles === "object" ? parsed.profiles : {},
        };
    } catch (error) {
        return { lastKey: "", profiles: {} };
    }
};

const rememberResponderProfile = (answers = {}) => {
    const name = String(answers.name || "").trim();
    const email = String(answers.email_address || "").trim();
    const lodge = String(answers.lodge_affiliation || "").trim();
    const otherLodge = String(answers.lodge_affiliation__other || "").trim();
    const keys = getResponderMemoryKeys({ name, email });
    if (!keys.length || (!name && !email)) return;

    const storage = getBrowserStorage();
    if (!storage) return;
    const store = readResponderMemoryStore();
    const profile = {
        name,
        email,
        lodge,
        otherLodge,
        updatedAt: new Date().toISOString(),
    };
    keys.forEach((key) => {
        store.profiles[key] = {
            ...(store.profiles[key] || {}),
            ...profile,
        };
    });
    store.lastKey = keys[0];
    storage.setItem(RSVP_RESPONDER_MEMORY_KEY, JSON.stringify(store));
};

const getResponderMemorySuggestions = (search = "") => {
    const needle = normalizeMemoryKeyPart(search);
    if (!needle) return [];
    const store = readResponderMemoryStore();
    const seen = new Set();
    return Object.values(store.profiles || {})
        .filter((profile) => profile && typeof profile === "object")
        .filter((profile) => {
            const haystack = normalizeMemoryKeyPart([profile.name, profile.email, profile.lodge, profile.otherLodge].filter(Boolean).join(" "));
            return haystack.includes(needle);
        })
        .sort((a, b) => String(b.updatedAt || "").localeCompare(String(a.updatedAt || "")))
        .filter((profile) => {
            const key = getResponderMemoryKeys({ name: profile.name, email: profile.email })[0]
                || normalizeMemoryKeyPart(`${profile.name}|${profile.email}|${profile.lodge}`);
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        })
        .slice(0, 5);
};

const responderProfileMatchesMember = (profile, member) => {
    const profileEmail = normalizeMemoryKeyPart(profile?.email);
    const memberEmail = normalizeMemoryKeyPart(member?.email);
    if (profileEmail && memberEmail && profileEmail === memberEmail) return true;

    const profileName = normalizeMemoryKeyPart(profile?.name);
    const memberName = normalizeMemoryKeyPart(member?.name);
    return Boolean(profileName && memberName && profileName === memberName);
};

const getResponderProfileLodgeLabel = (profile = {}) => (
    profile.lodge === "Other"
        ? profile.otherLodge || profile.lodge
        : profile.lodge
);

const getAttendancePreferenceForRole = (preference = {}, role = "") => {
    if (!preference || typeof preference !== "object") return {};
    if (role === "Visitor") return preference.visitor || preference;
    if (role === "Member of this lodge") return preference.member || preference;
    return {};
};

const hasAttendancePreferenceValues = (preference = {}, role = "") => {
    const rolePreference = getAttendancePreferenceForRole(preference, role);
    return Boolean(
        rolePreference
        && typeof rolePreference === "object"
        && [
            "member_category",
            "member_category__other",
            "masonic_style",
            "masonic_style__other",
            "masonic_rank",
            "masonic_rank__other",
            "lodge_affiliation",
            "lodge_affiliation__other",
        ].some((key) => String(rolePreference[key] || "").trim())
    );
};

const hasAnyAttendancePreferenceValues = (preference = {}) => (
    hasAttendancePreferenceValues(preference, "Member of this lodge")
    || hasAttendancePreferenceValues(preference, "Visitor")
);

const applyAttendancePreferenceToAnswers = (answerBag = {}, preference = {}, role = "") => {
    const rolePreference = getAttendancePreferenceForRole(preference, role || answerBag.attendance_role);
    if (!hasAttendancePreferenceValues(preference, role || answerBag.attendance_role)) return answerBag;
    const next = { ...answerBag };
    const keys = role === "Visitor" || answerBag.attendance_role === "Visitor"
        ? [
            "lodge_affiliation",
            "lodge_affiliation__other",
            "masonic_style",
            "masonic_style__other",
            "masonic_rank",
            "masonic_rank__other",
        ]
        : [
            "member_category",
            "member_category__other",
            "masonic_style",
            "masonic_style__other",
            "masonic_rank",
            "masonic_rank__other",
        ];
    keys.forEach((key) => {
        const value = String(rolePreference[key] || "").trim();
        if (value) next[key] = key === "member_category" && value === "Officer" ? "Member" : value;
    });
    return next;
};

const parseRecipientEmails = (value) => {
    const rawItems = Array.isArray(value) ? value : String(value || "").split(/[,\n;]/);
    const seen = new Set();
    return rawItems
        .flatMap((item) => {
            if (item && typeof item === "object") {
                return [item.email, item.value, item.label].filter(Boolean);
            }
            return String(item || "").split(/[,\n;]/);
        })
        .map((item) => String(item || "").trim())
        .map((item) => item.match(/<([^>]+)>/)?.[1] || item)
        .map((item) => item.trim().toLowerCase())
        .filter((item) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(item))
        .filter((item) => {
            if (seen.has(item)) return false;
            seen.add(item);
            return true;
        });
};

const getRsvpAnswer = (response, id) => response?.answers?.[id] || "";

const formatSurveyPercent = (count, total) => {
    if (!total) return "0%";
    return `${Math.round((Number(count || 0) / Number(total || 0)) * 100)}%`;
};

const surveyRows = (counts = {}) =>
    Object.entries(counts)
        .map(([label, count]) => ({ label, count: Number(count || 0) }))
        .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));

const getSurveyQuestion = (results, id) => (results?.questions || []).find((question) => question.id === id);

const surveyBarOption = (question, title) => {
    const rows = surveyRows(question?.counts).slice(0, 8).reverse();
    return {
        title: { text: title || question?.label || "", left: 0, textStyle: { fontSize: 14, color: "#0f172a" } },
        tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
        grid: { top: 42, right: 20, bottom: 20, left: 140 },
        xAxis: { type: "value", minInterval: 1, axisLabel: { color: "#64748b" } },
        yAxis: { type: "category", data: rows.map((row) => row.label), axisLabel: { color: "#475569", width: 130, overflow: "truncate" } },
        series: [{ type: "bar", data: rows.map((row) => row.count), itemStyle: { color: "#2f6c45", borderRadius: [0, 5, 5, 0] }, label: { show: true, position: "right" } }]
    };
};

const surveyDonutOption = (question, title) => ({
    title: { text: title || question?.label || "", left: 0, textStyle: { fontSize: 14, color: "#0f172a" } },
    tooltip: { trigger: "item" },
    legend: { bottom: 0, type: "scroll", textStyle: { color: "#475569" } },
    series: [{
        type: "pie",
        radius: ["42%", "68%"],
        center: ["50%", "46%"],
        data: surveyRows(question?.counts).map((row) => ({ name: row.label, value: row.count })),
        label: { formatter: "{b}: {c}" },
        itemStyle: { borderColor: "#fff", borderWidth: 2 }
    }]
});

const surveyMatrixOption = (question) => {
    const scale = ["Not at all", "Slightly", "Moderately", "Mostly", "Completely"];
    const prompts = Object.keys(question?.matrix || {});
    return {
        title: { text: "Experience Rating Profile", left: 0, textStyle: { fontSize: 14, color: "#0f172a" } },
        tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
        legend: { top: 26, textStyle: { color: "#475569" } },
        grid: { top: 74, right: 20, bottom: 16, left: 190 },
        xAxis: { type: "value", minInterval: 1, axisLabel: { color: "#64748b" } },
        yAxis: { type: "category", data: prompts, axisLabel: { color: "#475569", width: 180, overflow: "truncate" } },
        series: scale.map((label) => ({
            name: label,
            type: "bar",
            stack: "rating",
            data: prompts.map((prompt) => Number(question?.matrix?.[prompt]?.[label] || 0))
        }))
    };
};

function getOptions(question, answers, questionsById) {
    if (question.options) return question.options;
    if (question.optionsFrom) {
        const source = questionsById[question.optionsFrom];
        const selected = answers[question.optionsFrom];
        if (Array.isArray(selected) && selected.length) return selected;
        return source?.options || [];
    }
    return [];
}

function isOtherSurveyOption(value) {
    return String(value || "").toLowerCase().startsWith("other");
}

function allowsOtherText(question) {
    return question?.allowOtherText !== false;
}

function applyMultiChoiceRules(question, values, changedValue = null, checked = false) {
    const exclusiveOptions = question.exclusiveOptions || [];
    const groups = question.mutuallyExclusiveGroups || [];
    let next = Array.isArray(values) ? values.filter(Boolean) : [];

    if (checked && exclusiveOptions.includes(changedValue)) {
        return [changedValue];
    }

    if (checked && exclusiveOptions.length) {
        next = next.filter((item) => !exclusiveOptions.includes(item));
    }

    if (checked) {
        groups.forEach((group) => {
            if (group.includes(changedValue)) {
                next = next.filter((item) => !group.includes(item));
            }
        });
        if (changedValue && !next.includes(changedValue)) next = [...next, changedValue];
    }

    return next;
}

function getQuestionInstruction(question) {
    if (question.id === "name") return "Enter your first name and surname.";
    if (question.inputControl === "email") return "Enter an email address.";
    if (question.id === "attendance_role") return "Choose whether you are a member of this lodge or a visitor.";
    if (question.id === "member_category") return "Choose your membership type. Your office for this meeting is recorded separately.";
    if (question.id === "member_acting_officer") return "Tick Yes only if you are serving in an office at this meeting. Then choose office holder or acting and the office.";
    if (question.id === "visitor_acting_officer") return "Choose Yes if you are acting in an office at this meeting; otherwise choose No.";
    if (question.id === "masonic_style") return "Optional: choose the title used before your name in minutes.";
    if (question.id === "masonic_rank") return "Optional: choose your Craft or Provincial rank/status.";
    if (question.id === "office_or_capacity") return "Choose the current lodge office being recorded for the minutes.";
    if (question.id === "visitor_office_or_capacity") return "Choose the office you are acting in for the minutes.";
    if (question.id === "attendance_notes") return "Optional: add anything the Secretary should note.";
    if (question.id === "apology_reason") return "Optional: add an apology or reason for the Secretary and minutes.";
    if (question.id === "lodge_affiliation") return "Select your lodge. If you are a member of the public, choose My Lodge/body is not listed and enter \"Other\" below.";
    if (question.type === "multi") {
        return question.max ? `Select up to ${question.max}.` : "Select all that apply.";
    }
    if (question.type === "single") return "Select one option.";
    if (question.type === "matrix") return "Select one rating for each statement.";
    if (question.type === "textarea") return "Write your answer in the box below.";
    return "Enter a short answer.";
}

function getSectionTheme(section) {
    return section?.theme || section?.title?.toLowerCase().replace(/[^a-z0-9]+/g, "-") || "default";
}

function getSectionThemeLabel(section) {
    const labels = {
        background: "Background",
        before: "Before Joining",
        current: "Current",
        barriers: "Barriers",
    };
    return labels[getSectionTheme(section)] || "Survey";
}

function hasSurveyModeratorAccess(user) {
    return ["admin", "archivist", "secretary"].includes(String(user?.role || "").trim().toLowerCase());
}

function hasSurveyBuilderAccess(user) {
    return ["admin", "archivist", "secretary", "member"].includes(String(user?.role || "").trim().toLowerCase());
}

function hasAuthenticatedSurveyAccess(user) {
    const role = String(user?.role || "").trim().toLowerCase();
    return Boolean(user) && role && role !== "user";
}

function hasClosedSurveyResultsAccess(user) {
    return ["admin", "archivist", "secretary", "member"].includes(String(user?.role || "").trim().toLowerCase());
}

function hasRsvpReviewAccess(user) {
    const role = String(user?.role || "").trim().toLowerCase();
    return Boolean(user) && role && role !== "user";
}

function hasSurveyEmailTestAccess(user) {
    return String(user?.role || "").trim().toLowerCase() === "archivist";
}

function getMeetingFormNoun(item) {
    return isDigitalAttendanceKind(item?.kind) ? "attendance register" : "RSVP forecast";
}

function getMeetingFormTitleCase(item) {
    return isDigitalAttendanceKind(item?.kind) ? "Attendance Register" : "RSVP Forecast";
}

function isQuestionAnswered(question, answerBag = {}) {
    if (!isQuestionVisible(question, answerBag)) {
        return true;
    }

    const value = answerBag[question.id];
    if (!isQuestionRequired(question)
        && (value === undefined
            || value === null
            || String(value || "").trim() === ""
            || (Array.isArray(value) && value.length === 0))) {
        return true;
    }
    if (question.id === "name") {
        return String(value || "").trim().split(/\s+/).filter(Boolean).length >= 2;
    }

    if (question.type === "single" && isOtherSurveyOption(value)) {
        return Boolean(String(answerBag[`${question.id}__other`] || "").trim());
    }

    if (question.type === "multi") {
        if (!Array.isArray(value) || value.length === 0) return false;
        if (value.some(isOtherSurveyOption)) return Boolean(String(answerBag[`${question.id}__other`] || "").trim());
        return true;
    }

    if (question.type === "matrix") {
        const prompts = question.prompts || [];
        return prompts.length > 0 && prompts.every((prompt) => Boolean(value?.[prompt]));
    }

    if (question.type === "text" || question.type === "textarea") {
        return Boolean(String(value || "").trim());
    }

    return value !== undefined && value !== null && value !== "";
}

function isQuestionRequired(question) {
    if (question?.required === false) return false;
    if (question?.required === true) return true;
    return !/\(optional\)/i.test(String(question?.label || ""));
}

function isQuestionVisible(question, answerBag = {}) {
    const matchesCondition = (condition) => {
        if (!condition) return true;
        return answerBag[condition.questionId] === condition.value;
    };
    if (Array.isArray(question?.showWhenAny)) return question.showWhenAny.some(matchesCondition);
    if (!question?.showWhen) return true;
    return matchesCondition(question.showWhen);
}

function pruneHiddenSurveyAnswers(answerBag = {}, questions = []) {
    const pruned = { ...answerBag };
    let changed = true;
    while (changed) {
        changed = false;
        for (const question of questions) {
            if (!question?.showWhen && !Array.isArray(question?.showWhenAny)) continue;
            if (isQuestionVisible(question, pruned)) continue;
            if (Object.prototype.hasOwnProperty.call(pruned, question.id)) {
                delete pruned[question.id];
                changed = true;
            }
            const otherKey = `${question.id}__other`;
            if (Object.prototype.hasOwnProperty.call(pruned, otherKey)) {
                delete pruned[otherKey];
                changed = true;
            }
        }
    }
    return pruned;
}

function BarList({ title, counts = {}, total = 0, note = "Bars show the relative response pattern; counts show how many respondents selected each answer." }) {
    const rows = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    if (!rows.length) return null;

    return (
        <div className="survey-results-card">
            <h3>{title}</h3>
            {note && <p className="survey-result-note">{note}</p>}
            <div className="survey-bars">
                {rows.map(([label, count]) => {
                    const percent = total ? Math.round((count / total) * 100) : 0;
                    return (
                        <div className="survey-bar-row" key={label}>
                            <div>
                                <span>{label}</span>
                                <strong>{count}</strong>
                            </div>
                            <div className="survey-bar-track">
                                <span style={{ width: `${Math.max(percent, count ? 5 : 0)}%` }} />
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function DonutBreakdown({ title, rows = [] }) {
    const total = rows.reduce((sum, row) => sum + Number(row.count || 0), 0);
    if (!rows.length || !total) return null;

    const colors = ["#14b8a6", "#2563eb", "#f59e0b", "#ef4444", "#8b5cf6", "#64748b", "#22c55e", "#ec4899"];
    let current = 0;
    const stops = rows.map((row, index) => {
        const start = current;
        const end = current + (Number(row.count || 0) / total) * 100;
        current = end;
        return `${colors[index % colors.length]} ${start}% ${end}%`;
    }).join(", ");

    return (
        <div className="survey-results-card survey-donut-card">
            <h3>{title}</h3>
            <div className="survey-donut-layout">
                <div
                    className="survey-donut"
                    style={{ background: `conic-gradient(${stops})` }}
                    aria-hidden="true"
                >
                    <span>{total}</span>
                </div>
                <div className="survey-donut-legend">
                    {rows.map((row, index) => (
                        <div key={row.label}>
                            <i style={{ background: colors[index % colors.length] }} />
                            <span>{row.label}</span>
                            <strong>{row.count}</strong>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function LikertStack({ question }) {
    const scale = ["Not at all", "Slightly", "Moderately", "Mostly", "Completely"];
    const rows = Object.entries(question.matrix || {}).map(([prompt, counts]) => {
        const total = Object.values(counts).reduce((sum, count) => sum + Number(count || 0), 0);
        const weighted = scale.reduce((sum, label, index) => sum + Number(counts[label] || 0) * (index + 1), 0);
        return {
            prompt,
            counts,
            total,
            average: total ? weighted / total : 0,
        };
    }).sort((a, b) => b.average - a.average);
    if (!rows.length) return null;

    return (
        <div className="survey-results-card wide survey-likert-card">
            <div className="survey-likert-header">
                <div>
                    <h3>{question.label}</h3>
                    <p>Agreement score is shown from 1 to 5. The coloured segments show how responses are distributed across the scale.</p>
                </div>
                <div className="survey-likert-legend">
                    {scale.map((label, index) => (
                        <span key={label}>
                            <i className={`tone-${index}`} />
                            {label}
                        </span>
                    ))}
                </div>
            </div>
            <div className="survey-likert-list">
                {rows.map(({ prompt, counts, total, average }) => {
                    return (
                        <div className="survey-likert-row" key={prompt}>
                            <div className="survey-likert-title">
                                <strong>{prompt}</strong>
                                <span>{average ? average.toFixed(1) : "n/a"} / 5 · {total} response{total === 1 ? "" : "s"}</span>
                            </div>
                            <div className="survey-likert-stack">
                                {scale.map((label, index) => {
                                    const count = Number(counts[label] || 0);
                                    const percent = total ? (count / total) * 100 : 0;
                                    return (
                                        <span
                                            key={label}
                                            className={`tone-${index}`}
                                            style={{ width: `${percent}%` }}
                                            title={`${label}: ${count}`}
                                        />
                                    );
                                })}
                            </div>
                            <div className="survey-likert-counts">
                                {scale.map((label) => (
                                    <span key={label}>{label}: <strong>{counts[label] || 0}</strong></span>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function getQuestionMap(results) {
    return Object.fromEntries((results?.questions || []).map((question) => [question.id, question]));
}

function sumCounts(question, labels) {
    return labels.reduce((sum, label) => sum + Number(question?.counts?.[label] || 0), 0);
}

function pct(count, total) {
    if (!total) return 0;
    return Math.round((count / total) * 100);
}

function InsightMetric({ label, value, detail, tone = "neutral" }) {
    return (
        <div className={`survey-insight-metric ${tone}`}>
            <strong>{value}</strong>
            <span>{label}</span>
            {detail && <small>{detail}</small>}
        </div>
    );
}

function ClusterBars({ title, clusters, total }) {
    const rows = clusters
        .map((cluster) => ({ ...cluster, percent: pct(cluster.count, total) }))
        .sort((a, b) => b.count - a.count);

    return (
        <div className="survey-results-card survey-cluster-card">
            <h3>{title}</h3>
            <div className="survey-cluster-list">
                {rows.map((row) => (
                    <div className="survey-cluster-row" key={row.label}>
                        <div>
                            <strong>{row.label}</strong>
                            <span>{row.count} mention{row.count === 1 ? "" : "s"}</span>
                        </div>
                        <div className="survey-cluster-track">
                            <span style={{ width: `${Math.max(row.percent, row.count ? 6 : 0)}%` }} />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

const ENGAGEMENT_HEALTH_MAP = {
    overall_experience: {
        shortLabel: "Overall experience",
        positive: ["Better than expected", "Much better than expected"],
        neutral: ["About as expected"],
        negative: ["Much worse than expected", "Worse than expected"],
    },
    current_activity: {
        shortLabel: "Current activity",
        positive: ["Very active", "Regularly involved", "Moderately active"],
        neutral: ["Occasionally involved", "Occasionally active"],
        negative: ["Rarely involved", "Rarely active", "No longer active"],
    },
    connected_to_lodge: {
        shortLabel: "Lodge connection",
        positive: ["Very", "Extremely"],
        neutral: ["Moderately"],
        negative: ["Not at all", "Slightly"],
    },
    engagement_five_years: {
        shortLabel: "5-year engagement",
        positive: ["Increased significantly", "Increased slightly"],
        neutral: ["Stayed the same"],
        negative: ["Decreased slightly", "Decreased significantly"],
    },
    recommend_to_friend: {
        shortLabel: "Recommend / join again",
        positive: ["Definitely", "Probably"],
        neutral: ["Unsure"],
        negative: ["Probably not", "Definitely not"],
    },
};

function EngagementHealthChart({ questions }) {
    const rows = questions
        .map((question) => {
            const map = ENGAGEMENT_HEALTH_MAP[question.id];
            if (!map) return null;
            const positive = sumCounts(question, map.positive);
            const neutral = sumCounts(question, map.neutral);
            const negative = sumCounts(question, map.negative);
            const total = positive + neutral + negative;
            return {
                label: map.shortLabel,
                total,
                positive,
                neutral,
                negative,
                counts: { positive, neutral, negative },
            };
        })
        .filter(Boolean);

    if (!rows.length) return null;

    const option = {
        tooltip: {
            trigger: "axis",
            axisPointer: { type: "shadow" },
            formatter: (params) => {
                const row = rows[params[0].dataIndex];
                return [
                    `<strong>${row.label}</strong>`,
                    `Positive: ${row.counts.positive}`,
                    `Neutral: ${row.counts.neutral}`,
                    `Concern: ${row.counts.negative}`,
                ].join("<br/>");
            },
        },
        legend: { top: 0, textStyle: { color: "#334155", fontWeight: 700 } },
        grid: { left: 150, right: 24, top: 44, bottom: 24 },
        xAxis: { type: "value", minInterval: 1, axisLabel: { formatter: "{value}" } },
        yAxis: {
            type: "category",
            inverse: true,
            data: rows.map((row) => row.label),
            axisLabel: { color: "#334155", fontWeight: 700 },
        },
        series: [
            { name: "Concern", type: "bar", stack: "total", data: rows.map((row) => row.negative), itemStyle: { color: "#ef4444" } },
            { name: "Neutral", type: "bar", stack: "total", data: rows.map((row) => row.neutral), itemStyle: { color: "#facc15" } },
            { name: "Positive", type: "bar", stack: "total", data: rows.map((row) => row.positive), itemStyle: { color: "#14b8a6" } },
        ],
    };

    return (
        <div className="survey-results-card wide survey-health-card">
            <div className="survey-health-header">
                <h3>Engagement and Retention Health</h3>
                <p>One combined view of the core retention questions. Each row is grouped into concern, neutral, and positive signals.</p>
            </div>
            <ReactECharts option={option} notMerge lazyUpdate style={{ width: "100%", height: Math.max(340, rows.length * 48 + 90) }} />
        </div>
    );
}

function SurveyFlowMap({ flow, responseCount = 0 }) {
    const links = flow?.links || [];
    if (!links.length) return null;

    const sankeyData = buildSankeyData(flow);

    return (
        <div className="survey-results-card survey-flow-card">
            <div className="survey-flow-heading">
                <div>
                    <h3>{flow.title}</h3>
                    <p>{flow.description}</p>
                </div>
                <span>{(flow.stages || []).map(shortSankeyStageLabel).join(" -> ")}</span>
            </div>
            <div className="survey-sankey-shell">
                <ReactECharts
                    option={sankeyData.option}
                    notMerge
                    lazyUpdate
                    style={{ width: "100%", height: sankeyData.height }}
                />
            </div>
        </div>
    );
}

function shortSankeyStageLabel(label) {
    const labels = {
        "Age when joining": "Joining age",
        "Current age": "Now",
        "Current membership/affiliation": "Affiliation",
        "Highest degree": "Degree",
        "Current roles": "Roles",
        "Highest degree/status": "Degree",
        "Current lodge role": "Lodge role",
        "Provincial/Grand role": "PGL/GL role",
        "Membership/affiliation": "Affiliation",
        "Driving/Festive Board": "Driving/FB",
    };
    return labels[label] || label;
}

function shortSankeyNodeLabel(label) {
    const text = String(label || "");
    const replacements = [
        [/Worshipful Master \/ in the Chair/i, "WM / Chair"],
        [/Grand Lodge, Provincial, District, Regional or equivalent officer/i, "Higher-order officer"],
        [/Other higher-order Masonic role/i, "Other higher role"],
        [/I do not currently hold a role beyond my Craft Lodge/i, "Craft lodge only"],
        [/Provincial\/District officer/i, "PGL/DGL officer"],
        [/Grand Lodge officer/i, "GL officer"],
        [/Provincial\/District committee/i, "PGL/DGL committee"],
        [/Provincial\/District ceremonial role/i, "PGL/DGL ceremonial"],
        [/Member of more than one Craft lodge/i, "Multiple Craft lodges"],
        [/Member of one Craft lodge/i, "One Craft lodge"],
        [/Chapter\/other Masonic orders/i, "Chapter/other orders"],
        [/No current Provincial\/District\/Grand Lodge role/i, "No PGL/GL role"],
        [/No current lodge office/i, "No lodge office"],
    ];
    const replaced = replacements.reduce((value, [pattern, replacement]) => value.replace(pattern, replacement), text);
    if (replaced.length <= 24) return replaced;
    return `${replaced.slice(0, 21)}...`;
}

function buildSankeyData(flow) {
    const nodeMap = new Map();
    const linkMap = new Map();
    const colors = ["#0f766e", "#2563eb", "#f59e0b", "#ef4444", "#8b5cf6", "#64748b"];

    const addNode = (id, label, stage) => {
        if (!nodeMap.has(id)) {
            nodeMap.set(id, {
                name: id,
                label: { formatter: shortSankeyNodeLabel(label) },
                itemStyle: { color: colors[stage % colors.length] },
                depth: stage,
            });
        }
    };

    const addLink = (source, target, value) => {
        if (!source || !target || !value) return;
        const key = `${source}||${target}`;
        const current = linkMap.get(key) || { source, target, value: 0 };
        current.value += Number(value || 0);
        linkMap.set(key, current);
    };

    (flow.links || []).forEach((link) => {
        const sourceStage = Number.isInteger(link.sourceStage) ? link.sourceStage : 0;
        const targetStage = Number.isInteger(link.targetStage) ? link.targetStage : (link.via ? 2 : 1);
        const sourceId = `${sourceStage}:${link.source}`;
        addNode(sourceId, link.source, sourceStage);

        if (link.via) {
            const viaId = `1:${link.via}`;
            const targetId = `2:${link.target}`;
            addNode(viaId, link.via, 1);
            addNode(targetId, link.target, 2);
            addLink(sourceId, viaId, link.count);
            addLink(viaId, targetId, link.count);
        } else {
            const targetId = `${targetStage}:${link.target}`;
            addNode(targetId, link.target, targetStage);
            addLink(sourceId, targetId, link.count);
        }
    });

    const nodeCount = nodeMap.size;
    const stageCount = flow.stages?.length || 0;
    const height = Math.max(340, Math.min(stageCount > 6 ? 820 : 620, 180 + nodeCount * 32));

    return {
        height,
        option: {
            backgroundColor: "transparent",
            tooltip: {
                trigger: "item",
                triggerOn: "mousemove",
                formatter: (params) => {
                    if (params.dataType === "edge") {
                        const source = String(params.data.source || "").replace(/^\d+:/, "");
                        const target = String(params.data.target || "").replace(/^\d+:/, "");
                        return `${source}<br/>to ${target}<br/><strong>${params.data.value}</strong> response path${params.data.value === 1 ? "" : "s"}`;
                    }
                    return String(params.name || "").replace(/^\d+:/, "");
                },
            },
            series: [
                {
                    type: "sankey",
                    emphasis: { focus: "adjacency" },
                    nodeAlign: "justify",
                    nodeGap: stageCount > 6 ? 18 : 12,
                    nodeWidth: stageCount > 6 ? 14 : 18,
                    draggable: false,
                    layoutIterations: 64,
                    data: Array.from(nodeMap.values()),
                    links: Array.from(linkMap.values()),
                    lineStyle: {
                        color: "gradient",
                        curveness: 0.5,
                        opacity: 0.42,
                    },
                    label: {
                        color: "#334155",
                        fontSize: 12,
                        fontWeight: 700,
                        overflow: "break",
                    },
                },
            ],
        },
    };
}

function SurveyInsightDashboard({ results }) {
    const questionMap = getQuestionMap(results);
    const total = results?.responseCount || 0;
    const active = sumCounts(questionMap.current_activity, ["Very active", "Regularly involved", "Moderately active"]);
    const drifting = sumCounts(questionMap.current_activity, ["Rarely involved", "Rarely active", "No longer active"]);
    const connected = sumCounts(questionMap.connected_to_lodge, ["Very", "Extremely"]);
    const recommend = sumCounts(questionMap.recommend_to_friend, ["Definitely", "Probably"]);
    const worseThanExpected = sumCounts(questionMap.overall_experience, ["Much worse than expected", "Worse than expected"]);
    const barriers = questionMap.participation_barriers;
    const clusters = [
        {
            label: "Travel, cost, and time",
            count: sumCounts(barriers, ["Time, work, family, or health pressures", "Costs, petrol, or transport", "Distance, traffic, or weeknight travel", "Petrol or transport cost", "Distance to lodge", "Travel time", "Traffic", "Weeknight travel is difficult", "Visiting other lodges is too far", "Local lodge options are limited"])
        },
        {
            label: "Safety, parking, and driving back",
            count: sumCounts(barriers, ["Safety, parking, or access around venues", "Festive Board or driving-home concerns", "Safety or dodgy areas around the venue", "Parking/accessibility", "I avoid drinking at Festive Board because I must drive home", "Drink-driving concerns reduce the social aspect"])
        },
        {
            label: "Lodge experience",
            count: sumCounts(barriers, ["Meetings feel too long, repetitive, or finish too late", "Not enough learning, mentoring, or meaningful content", "Poor communication or requests not being answered", "Feeling excluded, not valued, or conflict between members", "Meetings are repetitive", "Meetings finish too late", "Lack of education", "Poor fellowship", "Conflict between members", "Poor leadership", "Poor communication", "I don't feel valued"])
        },
        {
            label: "Life stage and capacity",
            count: sumCounts(barriers, ["Work commitments", "Family commitments", "Health", "Financial cost", "Lack of time", "I have relocated"])
        },
        {
            label: "Pressure and obligation",
            count: sumCounts(barriers, ["Dominating personalities or one-way expectations", "Pressure to visit or support other lodges", "Too much administration or office pressure", "Feeling guilty when I cannot visit or support", "Office progression became an obligation", "Too much administration"])
        },
        {
            label: "Future confidence",
            count: sumCounts(barriers, ["Concerns about membership decline or the future", "Freemasonry has not met my expectations", "Membership decline", "Lack of younger members", "Organisation resistant to change", "My interests have changed"])
        }
    ];

    return (
        <div className="survey-results-card wide survey-insight-dashboard">
            <div className="survey-insight-header">
                <div>
                    <span>Insight Summary</span>
                    <h3>What the responses are starting to say</h3>
                </div>
                <small>{total} anonymous response{total === 1 ? "" : "s"}</small>
            </div>
            <div className="survey-insight-metrics">
                <InsightMetric label="active or regularly involved" value={active} detail={`${total} response${total === 1 ? "" : "s"}`} tone="good" />
                <InsightMetric label="feel very/extremely connected" value={connected} detail={`${total} response${total === 1 ? "" : "s"}`} tone="good" />
                <InsightMetric label="recommend / choose again" value={recommend} detail={`${total} response${total === 1 ? "" : "s"}`} tone="good" />
                <InsightMetric label="rarely/no longer active" value={drifting} detail={`${total} response${total === 1 ? "" : "s"}`} tone={drifting ? "watch" : "neutral"} />
                <InsightMetric label="worse than expected" value={worseThanExpected} detail={`${total} response${total === 1 ? "" : "s"}`} tone={worseThanExpected ? "watch" : "neutral"} />
            </div>
            {barriers && <ClusterBars title="Barrier Themes" clusters={clusters} total={total} />}
        </div>
    );
}

function SurveyQuestionAppendix({ question }) {
    if (question.type === "matrix") {
        return (
            <div className="survey-live-appendix-matrix">
                {Object.entries(question.matrix || {}).map(([prompt, counts]) => (
                    <div key={prompt}>
                        <strong>{prompt}</strong>
                        <div>
                            {surveyRows(counts).map((row) => (
                                <span key={row.label}>{row.label}: {row.count}</span>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    const rows = surveyRows(question.counts);
    if (rows.length) {
        return (
            <div className="survey-live-bars">
                {rows.map((row) => (
                    <div className="survey-live-bar" key={row.label}>
                        <div>
                            <span>{row.label}</span>
                            <strong>{row.count}</strong>
                        </div>
                        <div className="survey-live-track">
                            <span style={{ width: formatSurveyPercent(row.count, question.total) }} />
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    if (question.textResponses?.length) {
        return (
            <ul className="survey-live-text-list">
                {question.textResponses.slice(0, 40).map((text, index) => (
                    <li key={`${question.id}-${index}`}>{text}</li>
                ))}
            </ul>
        );
    }

    return <p className="survey-muted">No responses recorded for this question.</p>;
}

function ClosedSurveyAnalysis({ results }) {
    const analysis = results?.analysis || {};
    const keyMetrics = analysis.keyMetrics || [];
    const reportSections = analysis.reportSections || [];
    const findings = analysis.findings || [];
    const recommendationQuestion = getSurveyQuestion(results, "recommend_to_friend");
    const activityQuestion = getSurveyQuestion(results, "current_activity");
    const connectionQuestion = getSurveyQuestion(results, "connected_to_lodge");
    const barrierQuestion = getSurveyQuestion(results, "participation_barriers");
    const motivationQuestion = getSurveyQuestion(results, "motivation_joining");
    const experienceMatrixQuestion = getSurveyQuestion(results, "experience_ratings");
    const hasChartData = Boolean(
        recommendationQuestion?.total ||
        activityQuestion?.total ||
        connectionQuestion?.total ||
        barrierQuestion?.total ||
        motivationQuestion?.total ||
        experienceMatrixQuestion?.total
    );

    return (
        <section className="survey-results survey-closed-analysis">
            <div className="survey-closed-banner">
                <span>Closed</span>
                <strong>{results.responseCount}</strong>
                <em>anonymous responses</em>
            </div>

            <div className="survey-results-card wide survey-analysis-card">
                <h3>{results.title}</h3>
                {analysis.closingStatement && (
                    <blockquote>{analysis.closingStatement}</blockquote>
                )}
                {(analysis.summary || []).map((line) => (
                    <p key={line}>{line}</p>
                ))}
                {analysis.methodNote && <p>{analysis.methodNote}</p>}
            </div>

            {keyMetrics.length > 0 && (
                <div className="survey-analysis-metrics">
                    {keyMetrics.map((metric) => (
                        <div className="survey-results-card" key={metric.label}>
                            <strong>{metric.value}</strong>
                            <span>{metric.label}</span>
                            <small>{metric.detail}</small>
                        </div>
                    ))}
                </div>
            )}

            {reportSections.map((section) => (
                <div className="survey-results-card wide survey-report-section" key={section.title}>
                    <h3>{section.title}</h3>
                    <p>{section.body}</p>
                    {section.evidence?.length > 0 && (
                        <div className="survey-report-evidence">
                            {section.evidence.map((item) => (
                                <span key={`${section.title}-${item.label}`}>
                                    {item.label}: {item.count}
                                    {item.percent !== null && item.percent !== undefined ? ` (${item.percent}%)` : ""}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            ))}

            {findings.length > 0 && (
                <div className="survey-results-card wide survey-report-section">
                    <h3>Key findings</h3>
                    <div className="survey-finding-list">
                        {findings.map((finding) => (
                            <article key={finding.title}>
                                <strong>{finding.title}</strong>
                                <p>{finding.body}</p>
                                {finding.evidence?.length > 0 && (
                                    <div className="survey-report-evidence">
                                        {finding.evidence.map((item) => (
                                            <span key={`${finding.title}-${item.label}`}>
                                                {item.label}: {item.count}
                                                {item.percent !== null && item.percent !== undefined ? ` (${item.percent}%)` : ""}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </article>
                        ))}
                    </div>
                </div>
            )}

            {hasChartData && (
                <section className="survey-live-charts" aria-label="Survey charts">
                    <div className="survey-live-chart-card">
                        <ReactECharts option={surveyDonutOption(recommendationQuestion, "Recommendation / Join Again")} style={{ height: 320 }} />
                    </div>
                    <div className="survey-live-chart-card">
                        <ReactECharts option={surveyBarOption(connectionQuestion, "Connection to Lodge")} style={{ height: 320 }} />
                    </div>
                    <div className="survey-live-chart-card">
                        <ReactECharts option={surveyBarOption(activityQuestion, "Current Engagement")} style={{ height: 320 }} />
                    </div>
                    <div className="survey-live-chart-card wide">
                        <ReactECharts option={surveyMatrixOption(experienceMatrixQuestion)} style={{ height: 360 }} />
                    </div>
                    <div className="survey-live-chart-card">
                        <ReactECharts option={surveyBarOption(motivationQuestion, "Why Brethren Joined")} style={{ height: 360 }} />
                    </div>
                    <div className="survey-live-chart-card">
                        <ReactECharts option={surveyBarOption(barrierQuestion, "Participation Barriers")} style={{ height: 360 }} />
                    </div>
                </section>
            )}

            {analysis.recommendations?.length > 0 && (
                <div className="survey-results-card wide survey-report-section">
                    <h3>Suggested next steps</h3>
                    <ul>
                        {analysis.recommendations.map((recommendation) => (
                            <li key={recommendation}>{recommendation}</li>
                        ))}
                    </ul>
                </div>
            )}

            {results.relationshipFlows?.length > 0 && (
                <>
                    <h2 className="survey-results-heading">Linked Response Patterns</h2>
                    <div className="survey-alert flow-note">
                        Sankey ribbons aggregate anonymous responses. Thicker ribbons mean more people shared the same route across answers.
                    </div>
                    <div className="survey-flow-grid">
                        {results.relationshipFlows.map((flow) => (
                            <SurveyFlowMap key={flow.id} flow={flow} responseCount={results.responseCount} />
                        ))}
                    </div>
                </>
            )}

            <section className="survey-live-appendix" aria-label="Survey appendix">
                <div>
                    <p className="survey-eyebrow">Appendix</p>
                    <h3>Questions and Aggregate Results</h3>
                    <p>{analysis.appendixNote}</p>
                </div>
                {(results.questions || []).map((question) => (
                    <details className="survey-results-card" key={question.id}>
                        <summary>
                            <span>{question.section}</span>
                            <strong>{question.label}</strong>
                            <em>{question.total} response{question.total === 1 ? "" : "s"}</em>
                        </summary>
                        <SurveyQuestionAppendix question={question} />
                    </details>
                ))}
            </section>
        </section>
    );
}

function isMeetingAttendanceResults(results, selectedSurvey) {
    return isMeetingFormSurveyKind(results?.kind) || isMeetingFormSurveyKind(selectedSurvey?.kind);
}

function isDigitalAttendanceResults(results, selectedSurvey) {
    return isDigitalAttendanceKind(results?.kind) || isDigitalAttendanceKind(selectedSurvey?.kind);
}

function formatAttendanceName(response) {
    const name = [response.style, response.name || "Not given"].filter(Boolean).join(" ");
    return name;
}

function formatRankOffice(response) {
    const office = response.officeOrCapacity
        ? [response.officerRole && response.officerRole !== "Office holder" ? response.officerRole : null, response.officeOrCapacity].filter(Boolean).join(": ")
        : "";
    return [response.rank, office].filter(Boolean).join(" · ");
}

function formatMinutesPerson(response) {
    const name = formatAttendanceName(response);
    return response.rank ? `${name}, ${response.rank}` : name;
}

function formatAttendancePersonDetail(response) {
    return [formatAttendanceName(response), response.rank].filter(Boolean).join(" - ");
}

function formatOfficeHolder(response) {
    const person = formatAttendancePersonDetail(response);
    if (response.officerRole && response.officerRole !== "Office holder") {
        return `${response.officerRole} (${person})`;
    }
    return person;
}

const ATTENDANCE_OFFICE_ORDER = [
    "Worshipful Master",
    "Immediate Past Master",
    "Senior Warden",
    "Junior Warden",
    "Secretary",
    "Treasurer",
    "Director of Ceremonies",
    "Steward of Charities",
    "Senior Steward",
    "Junior Steward",
    "Steward",
    "Almoner",
    "Chaplain",
    "Senior Deacon",
    "Junior Deacon",
    "Inner Guard",
    "Tyler",
    "Organist",
    "Mentor",
];

const ATTENDANCE_OFFICE_ALIASES = {
    wm: "Worshipful Master",
    "worshipful master": "Worshipful Master",
    ipm: "Immediate Past Master",
    "immediate past master": "Immediate Past Master",
    sw: "Senior Warden",
    "senior warden": "Senior Warden",
    jw: "Junior Warden",
    "junior warden": "Junior Warden",
    sec: "Secretary",
    secretary: "Secretary",
    treas: "Treasurer",
    treasurer: "Treasurer",
    dc: "Director of Ceremonies",
    "director of ceremonies": "Director of Ceremonies",
    stwdch: "Steward of Charities",
    "steward of charities": "Steward of Charities",
    stwd: "Steward",
    steward: "Steward",
    "senior steward": "Senior Steward",
    "junior steward": "Junior Steward",
    alm: "Almoner",
    almoner: "Almoner",
    chap: "Chaplain",
    chaplain: "Chaplain",
    sd: "Senior Deacon",
    "senior deacon": "Senior Deacon",
    jd: "Junior Deacon",
    "junior deacon": "Junior Deacon",
    ig: "Inner Guard",
    "inner guard": "Inner Guard",
    tyler: "Tyler",
    organist: "Organist",
    mentor: "Mentor",
};

function getAttendanceOfficeSortKey(office) {
    const cleanOffice = String(office || "").trim();
    const normalized = cleanOffice.toLowerCase();
    const canonical = ATTENDANCE_OFFICE_ALIASES[normalized] || cleanOffice;
    const index = ATTENDANCE_OFFICE_ORDER.findIndex((label) => label.toLowerCase() === canonical.toLowerCase());
    return index >= 0 ? index : ATTENDANCE_OFFICE_ORDER.length;
}

function sortAttendanceOfficeRows(rows = []) {
    return [...rows].sort((a, b) => {
        const order = getAttendanceOfficeSortKey(a.officeOrCapacity) - getAttendanceOfficeSortKey(b.officeOrCapacity);
        if (order !== 0) return order;
        return formatAttendanceName(a).localeCompare(formatAttendanceName(b));
    });
}

const ATTENDANCE_MEMBER_GROUPS = [
    { key: "Past Master", label: "Past Masters" },
    { key: "Member", label: "Members" },
    { key: "Emeritus Member", label: "Emeritus Members" },
    { key: "Honorary Member", label: "Honorary Members" },
];

function getAttendanceRollGroup(response) {
    const category = response?.memberCategory || "Member";
    if (category !== "Officer") return category;
    const rank = String(response?.rank || "").trim().toLowerCase();
    if (rank.includes("past master") || /\bpm\b/.test(rank)) return "Past Master";
    if (rank.includes("emeritus")) return "Emeritus Member";
    if (rank.includes("honorary")) return "Honorary Member";
    return "Member";
}

function getAttendancePersonKey(response) {
    const email = String(response?.email || "").trim().toLowerCase();
    if (email) return `email:${email}`;
    const name = String(response?.name || "").trim().toLowerCase().replace(/\s+/g, " ");
    const lodge = String(response?.lodge || "").trim().toLowerCase().replace(/\s+/g, " ");
    return `${name}|${lodge}`;
}

function uniqueAttendancePeople(rows) {
    const seen = new Set();
    return (rows || []).filter((response) => {
        const key = getAttendancePersonKey(response);
        if (!key || key === "|") return true;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
}

function buildAttendanceMinutesText(results, responses) {
    const rows = responses || [];
    const members = rows.filter((response) => String(response.role || response.attendance || "").trim() !== "Visitor");
    const visitors = rows.filter((response) => String(response.role || response.attendance || "").trim() === "Visitor");
    const uniqueMembers = uniqueAttendancePeople(members);
    const uniqueVisitors = uniqueAttendancePeople(visitors);
    const offices = sortAttendanceOfficeRows(rows.filter((response) => String(response.officeOrCapacity || "").trim()));
    const parts = [];

    if (results?.meeting) {
        parts.push([
            results.meeting.lodge && `Lodge: ${results.meeting.lodge}`,
            results.meeting.date && `Meeting date: ${results.meeting.date}`,
            results.meeting.type && `Meeting type: ${results.meeting.type}`,
        ].filter(Boolean).join("\n"));
    }

    if (offices.length) {
        parts.push([
            "Officers / capacities recorded:",
            ...offices.map((response) => `${response.officeOrCapacity}: ${formatOfficeHolder(response)}`),
        ].join("\n"));
    }

    parts.push([
        `Members recorded (${uniqueMembers.length}):`,
        ...(uniqueMembers.length
            ? uniqueMembers.map((response) => {
                const details = [response.memberCategory, response.lodge].filter(Boolean).join(", ");
                return `${formatMinutesPerson(response)}${details ? ` (${details})` : ""}`;
            })
            : ["None recorded"]),
    ].join("\n"));

    parts.push([
        `Visitors recorded (${uniqueVisitors.length}):`,
        ...(uniqueVisitors.length
            ? uniqueVisitors.map((response) => {
                const lodge = response.lodge || "lodge/body not given";
                return `${formatMinutesPerson(response)} - ${lodge}`;
            })
            : ["None recorded"]),
    ].join("\n"));

    const uniqueTotal = uniqueMembers.length + uniqueVisitors.length;
    parts.push(`A total of ${uniqueTotal} person${uniqueTotal === 1 ? "" : "s"} were digitally recorded in attendance.`);

    return parts.filter(Boolean).join("\n\n");
}

function DigitalAttendanceResults({ results, canManageSurveys = false, privateResponses = [], onLoadPrivateResponses = null }) {
    const roleQuestion = getSurveyQuestion(results, "attendance_role");
    const rows = privateResponses || [];
    const members = rows.filter((response) => String(response.role || response.attendance || "").trim() !== "Visitor");
    const visitors = rows.filter((response) => String(response.role || response.attendance || "").trim() === "Visitor");
    const uniqueMembers = uniqueAttendancePeople(members);
    const uniqueVisitors = uniqueAttendancePeople(visitors);
    const uniqueMemberCount = uniqueMembers.length || sumCounts(roleQuestion, ["Member of this lodge"]);
    const uniqueVisitorCount = uniqueVisitors.length || sumCounts(roleQuestion, ["Visitor"]);
    const roleRows = [
        { label: "Visitor", count: uniqueVisitorCount },
        { label: "Member of this lodge", count: uniqueMemberCount },
    ].filter((row) => row.count > 0);
    const lodgeBreakdown = results.attendanceByLodge || {};
    const hasLodgeBreakdown = Object.keys(lodgeBreakdown.memberByLodge || {}).length > 0
        || Object.keys(lodgeBreakdown.visitorByLodge || {}).length > 0;
    const officeRows = sortAttendanceOfficeRows(rows.filter((response) => String(response.officeOrCapacity || "").trim()));
    const memberGroups = ATTENDANCE_MEMBER_GROUPS.map((group) => ({
        ...group,
        rows: uniqueAttendancePeople(members.filter((response) => getAttendanceRollGroup(response) === group.key)),
    }));
    const otherMemberRows = members.filter(
        (response) => !ATTENDANCE_MEMBER_GROUPS.some((group) => getAttendanceRollGroup(response) === group.key)
    );
    const dayCount = Object.keys(results.submittedByDay || {}).length;

    const copyMinutesText = async () => {
        const text = buildAttendanceMinutesText(results, rows);
        try {
            await navigator.clipboard?.writeText(text);
        } catch (error) {
            console.error("Could not copy attendance register text:", error);
        }
    };

    return (
        <section className="survey-results survey-attendance-results">
            <div className="survey-summary-grid">
                <div>
                    <strong>{results.responseCount}</strong>
                    <span>people</span>
                </div>
                <div>
                    <strong>{uniqueMemberCount}</strong>
                    <span>members</span>
                </div>
                <div>
                    <strong>{uniqueVisitorCount}</strong>
                    <span>visitors</span>
                </div>
                <div>
                    <strong>{dayCount}</strong>
                    <span>response days</span>
                </div>
            </div>

            <div className="survey-insight-grid">
                <DonutBreakdown title="Attendance Register" rows={roleRows} />
                <div className="survey-results-card">
                    <h3>Minutes-ready attendance details</h3>
                    <p>
                        This view is shaped for a Secretary to check against the physical register, then copy into minutes. The physical register remains the official record.
                    </p>
                    <p className="survey-attendance-tally">
                        <strong>{uniqueMembers.length}</strong> member{uniqueMembers.length === 1 ? "" : "s"} · <strong>{uniqueVisitors.length}</strong> visitor{uniqueVisitors.length === 1 ? "" : "s"}
                    </p>
                    <div className="survey-private-actions">
                        {canManageSurveys && (
                            <>
                                <button type="button" className="secondary" onClick={onLoadPrivateResponses}>
                                    Refresh Register
                                </button>
                                <button type="button" className="secondary" onClick={copyMinutesText} disabled={!rows.length}>
                                    Copy Minutes Text
                                </button>
                            </>
                        )}
                    </div>
                    {results.meeting && (
                        <div className="survey-report-evidence">
                            {results.meeting.lodge && <span>Lodge: {results.meeting.lodge}</span>}
                            {results.meeting.date && <span>Date: {results.meeting.date}</span>}
                            {results.meeting.type && <span>Type: {results.meeting.type}</span>}
                        </div>
                    )}
                </div>
            </div>

            {hasLodgeBreakdown && (
                <div className="survey-results-grid">
                    <BarList
                        title="Members by lodge/body"
                        counts={lodgeBreakdown.memberByLodge || {}}
                        total={uniqueMemberCount}
                        note="Anonymous totals for people recorded as members of this attendance register."
                    />
                    <BarList
                        title="Visitors by lodge/body"
                        counts={lodgeBreakdown.visitorByLodge || {}}
                        total={uniqueVisitorCount}
                        note="Anonymous totals for people recorded as visitors."
                    />
                </div>
            )}

            {canManageSurveys && (
                <>
                    <div className="survey-results-card wide survey-minutes-card">
                        <div className="survey-minutes-head">
                            <h3>Attendance extract for minutes</h3>
                            <div className="survey-minutes-tools">
                                <span>{uniqueMembers.length} member{uniqueMembers.length === 1 ? "" : "s"} · {uniqueVisitors.length} visitor{uniqueVisitors.length === 1 ? "" : "s"}</span>
                                <button type="button" className="secondary" onClick={copyMinutesText} disabled={!rows.length}>
                                    Copy Extract
                                </button>
                            </div>
                        </div>

                        <div className="survey-office-register">
                            <div className="survey-office-register-heading">
                                <span>Office</span>
                                <span>Office Holder</span>
                            </div>
                            {officeRows.map((response) => (
                                <div className="survey-office-register-row" key={`office-${response.id}`}>
                                    <strong>{response.officeOrCapacity || "Not given"}</strong>
                                    <span>
                                        {formatOfficeHolder(response)}
                                        {response.note ? ` (${response.note})` : ""}
                                    </span>
                                </div>
                            ))}
                            {!officeRows.length && (
                                <div className="survey-office-register-row empty">
                                    <strong>Offices</strong>
                                    <span>No office holders have been recorded yet.</span>
                                </div>
                            )}
                        </div>

                        <div className="survey-member-register-grid">
                            {memberGroups.map((group) => (
                                <div className="survey-member-register-column" key={group.key}>
                                    <h4>{group.label}</h4>
                                    {group.rows.length ? (
                                        group.rows.map((response) => (
                                            <span key={response.id}>{formatAttendancePersonDetail(response)}</span>
                                        ))
                                    ) : (
                                        <em>-</em>
                                    )}
                                </div>
                            ))}
                        </div>

                        {otherMemberRows.length > 0 && (
                            <div className="survey-member-register-column survey-member-register-other">
                                <h4>Other Members</h4>
                                {otherMemberRows.map((response) => (
                                    <span key={response.id}>
                                        {[formatAttendancePersonDetail(response), response.memberCategory].filter(Boolean).join(" · ")}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="survey-results-card wide">
                        <h3>Visitors</h3>
                        <table className="survey-table survey-private-table survey-minutes-visitor-table">
                            <thead>
                            <tr>
                                <th>Name</th>
                                <th>Lodge</th>
                                <th>Rank</th>
                                <th>Note</th>
                            </tr>
                            </thead>
                            <tbody>
                            {visitors.map((response) => (
                                <tr key={response.id}>
                                    <td>{formatAttendanceName(response)}</td>
                                    <td>{response.lodge || "Not given"}</td>
                                    <td>{response.rank || ""}</td>
                                    <td>{response.note || ""}</td>
                                </tr>
                            ))}
                            {!visitors.length && (
                            <tr>
                                    <td colSpan="4">No visitors recorded.</td>
                                </tr>
                            )}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </section>
    );
}

function MeetingAttendanceResults({ results, canManageSurveys = false, privateResponses = [], onLoadPrivateResponses = null }) {
    if (isDigitalAttendanceResults(results, null)) {
        return (
            <DigitalAttendanceResults
                results={results}
                canManageSurveys={canManageSurveys}
                privateResponses={privateResponses}
                onLoadPrivateResponses={onLoadPrivateResponses}
            />
        );
    }

    const attendanceQuestion = getSurveyQuestion(results, "attendance_status");
    const attendanceRows = surveyRows(attendanceQuestion?.counts);
    const attendingCount = sumCounts(attendanceQuestion, ["Yes"]);
    const notAttendingCount = sumCounts(attendanceQuestion, ["No"]);
    const lodgeBreakdown = results.attendanceByLodge || {};
    const hasLodgeBreakdown = Object.keys(lodgeBreakdown.attendingByLodge || {}).length > 0
        || Object.keys(lodgeBreakdown.notAttendingByLodge || {}).length > 0;
    const publicQuestions = (results.questions || [])
        .filter((question) => !["name", "email_address", "attendance_status", "lodge_affiliation"].includes(question.id))
        .filter((question) => (
            (["single", "multi"].includes(question.type) && Object.keys(question.counts || {}).length) ||
            question.textResponses?.length
        ));

    return (
        <section className="survey-results survey-attendance-results">
            <div className="survey-summary-grid">
                <div>
                    <strong>{results.responseCount}</strong>
                    <span>responses</span>
                </div>
                <div>
                    <strong>{attendingCount}</strong>
                    <span>likely attending</span>
                </div>
                <div>
                    <strong>{notAttendingCount}</strong>
                    <span>not attending</span>
                </div>
                <div>
                    <strong>{Object.keys(results.submittedByDay || {}).length}</strong>
                    <span>response days</span>
                </div>
            </div>

            <div className="survey-insight-grid">
                <DonutBreakdown title="RSVP Forecast" rows={attendanceRows} />
                <div className="survey-results-card">
                    <h3>Private RSVP details</h3>
                    {canManageSurveys ? (
                        <>
                            <p>
                                Names and email addresses are visible here because you are logged in with RSVP review access. Public/token-only results stay anonymous.
                            </p>
                            <button type="button" className="secondary" onClick={onLoadPrivateResponses}>
                                Open RSVP Review
                            </button>
                        </>
                    ) : (
                        <p>
                            These figures are an experimental planning aid to help anticipate meeting requirements such as seating, catering, and visitor numbers. Names and email addresses are kept out of this anonymous results view; logged-in members with access can use RSVP Review to see the private response list.
                        </p>
                    )}
                    {results.meeting && (
                        <div className="survey-report-evidence">
                            {results.meeting.lodge && <span>Lodge: {results.meeting.lodge}</span>}
                            {results.meeting.date && <span>Date: {results.meeting.date}</span>}
                            {results.meeting.type && <span>Type: {results.meeting.type}</span>}
                        </div>
                    )}
                </div>
            </div>

            {canManageSurveys && (
                <div className="survey-results-card wide">
                    <h3>Private RSVP list</h3>
                    <table className="survey-table survey-private-table">
                        <thead>
                        <tr>
                            <th>Submitted</th>
                            <th>Changed</th>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Lodge</th>
                            <th>Attend</th>
                        </tr>
                        </thead>
                        <tbody>
                        {privateResponses.map((response) => (
                            <tr key={response.id}>
                                <td>{new Date(response.submitted_at).toLocaleString("en-ZA")}</td>
                                <td>{Number(response.revision_count || 0) > 0 ? `${response.revision_count} update${Number(response.revision_count || 0) === 1 ? "" : "s"}` : "No"}</td>
                                <td>{response.name || "Not given"}</td>
                                <td>{response.email || "Not given"}</td>
                                <td>{response.lodge || "Not given"}</td>
                                <td>{response.attendance || "Not given"}</td>
                            </tr>
                        ))}
                        {privateResponses.length === 0 && (
                            <tr>
                                <td colSpan="6">No private responses loaded yet. Use Open RSVP Review or the RSVP Review tab.</td>
                            </tr>
                        )}
                        </tbody>
                    </table>
                </div>
            )}

            {(hasLodgeBreakdown || publicQuestions.length > 0) && (
                <div className="survey-results-grid">
                    <BarList
                        title="Likely attending by lodge"
                        counts={lodgeBreakdown.attendingByLodge || {}}
                        total={attendingCount}
                        note="Anonymous lodge totals for respondents who selected Yes."
                    />
                    <BarList
                        title="Not attending by lodge"
                        counts={lodgeBreakdown.notAttendingByLodge || {}}
                        total={notAttendingCount}
                        note="Anonymous lodge totals for respondents who selected No."
                    />
                    {publicQuestions.map((question) => (
                        ["single", "multi"].includes(question.type)
                            ? <BarList key={question.id} title={question.label} counts={question.counts} total={question.total} />
                            : (
                                <div className="survey-results-card" key={question.id}>
                                    <h3>{question.label}</h3>
                                    <SurveyQuestionAppendix question={question} />
                                </div>
                            )
                    ))}
                </div>
            )}
        </section>
    );
}

function countPrivateResponsesBy(responses, key) {
    return responses.reduce((counts, response) => {
        const value = String(response?.[key] || "Not given").trim() || "Not given";
        counts[value] = (counts[value] || 0) + 1;
        return counts;
    }, {});
}

function looksLikeUuid(value) {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(value || "").trim());
}

function SurveyPage({ user, openLoginModal }) {
    const permanentLodgeId = useMemo(() => new URLSearchParams(window.location.search).get("lodge") || "", []);
    const initialSurveySlug = useMemo(() => new URLSearchParams(window.location.search).get("survey") || DEFAULT_SLUG, []);
    const [surveyList, setSurveyList] = useState([]);
    const [archivedSurveyList, setArchivedSurveyList] = useState([]);
    const [legacySurvey, setLegacySurvey] = useState(null);
    const [lodges, setLodges] = useState([]);
    const [recipientMembers, setRecipientMembers] = useState([]);
    const [selectedSlug, setSelectedSlug] = useState(initialSurveySlug);
    const [survey, setSurvey] = useState(null);
    const [token, setToken] = useState("");
    const [verifiedAccess, setVerifiedAccess] = useState(null);
    const [answers, setAnswers] = useState({});
    const [selectedAttendancePreference, setSelectedAttendancePreference] = useState(null);
    const [memberSuggestions, setMemberSuggestions] = useState([]);
    const [responderMemorySuggestions, setResponderMemorySuggestions] = useState([]);
    const [memberSuggesting, setMemberSuggesting] = useState(false);
    const [results, setResults] = useState(null);
    const [responses, setResponses] = useState([]);
    const [privateResponses, setPrivateResponses] = useState([]);
    const [summaryRecipientMemberEmails, setSummaryRecipientMemberEmails] = useState([]);
    const [summaryRecipientExtraEmails, setSummaryRecipientExtraEmails] = useState("");
    const [inviteRecipientMemberEmails, setInviteRecipientMemberEmails] = useState([]);
    const [inviteRecipientExtraEmails, setInviteRecipientExtraEmails] = useState("");
    const [inviteToken, setInviteToken] = useState("");
    const [editingResponseId, setEditingResponseId] = useState(null);
    const [editAnswers, setEditAnswers] = useState({});
    const [pendingCreate, setPendingCreate] = useState(null);
    const [pendingApologyPrompt, setPendingApologyPrompt] = useState(null);
    const [apologyDraft, setApologyDraft] = useState("");
    const [responderMemoryNotice, setResponderMemoryNotice] = useState("");
    const [builderRecipientPanels, setBuilderRecipientPanels] = useState({
        summary: false,
        invite: false,
    });
    const [builderTokenStatus, setBuilderTokenStatus] = useState({
        state: "empty",
        message: "Create a simple shared token.",
    });
    const [builderForm, setBuilderForm] = useState({
        kind: "meeting_attendance",
        title: "Meeting RSVP Forecast",
        lodge: "",
        meetingDate: "",
        meetingType: "Regular meeting",
        expiresAt: "",
        token: "",
        description: "",
        notificationMemberEmails: [],
        notificationEmails: "",
        inviteMemberEmails: [],
        inviteEmails: "",
        inviteImage: null,
    });
    const [inviteImageUploading, setInviteImageUploading] = useState(false);
    const [createdSurvey, setCreatedSurvey] = useState(null);
    const [closingDateEditor, setClosingDateEditor] = useState(null);
    const [savingClosingDate, setSavingClosingDate] = useState(false);
    const [status, setStatus] = useState("");
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [activeTab, setActiveTab] = useState("hub");
    const [surveyFilters, setSurveyFilters] = useState({
        lodge: "",
        kind: "all",
        includeArchived: false,
    });
    const lastAutoSubmittedKey = useRef("");

    useEffect(() => {
        axios.get("/api/surveys")
            .then(({ data }) => {
                const list = data || [];
                setSurveyList(list);
                if (permanentLodgeId) {
                    const lodge = list.find(item => item.meeting?.lodgeId === permanentLodgeId)?.meeting?.lodge;
                    setSurveyFilters(current => ({ ...current, lodge: lodge || permanentLodgeId, kind: "all" }));
                    requestAnimationFrame(() => document.getElementById("meeting-registers")?.scrollIntoView({ block: "start" }));
                }
            })
            .catch((err) => {
                console.error("Error loading surveys:", err);
                setError("Could not load available surveys.");
            });

        axios.get("/api/surveys/archived")
            .then(({ data }) => setArchivedSurveyList(permanentLodgeId ? [] : data || []))
            .catch((err) => {
                if (![401, 403].includes(err.response?.status)) {
                    console.error("Error loading archived RSVPs:", err);
                }
            });

        axios.get(`/api/surveys/${DEFAULT_SLUG}/public-results`)
            .then(({ data }) => setLegacySurvey(data))
            .catch((err) => {
                console.error("Error loading closed survey summary:", err);
            });

        axios.get("/api/lodges")
            .then(({ data }) => setLodges((data || []).map((lodge) => ({
                code: lodge.code,
                name: lodge.name,
                label: [lodge.name, lodge.code ? `No. ${lodge.code}` : null].filter(Boolean).join(" "),
            }))))
            .catch((err) => {
                console.error("Error loading lodges:", err);
            });
    }, []);

    const allSurveyList = useMemo(
        () => [...surveyList, ...archivedSurveyList],
        [archivedSurveyList, surveyList]
    );
    const selectedSurvey = allSurveyList.find((item) => item.slug === selectedSlug)
        || (selectedSlug === DEFAULT_SLUG
            ? {
                slug: DEFAULT_SLUG,
                title: legacySurvey?.title || "Behavioural Study of Freemasonry: Membership, Engagement and Retention",
                description: legacySurvey?.description || "Public summary of the anonymous membership, engagement, and retention survey.",
                kind: "standard",
            }
            : { slug: selectedSlug || DEFAULT_SLUG, kind: "standard" });
    const canModerate = hasSurveyModeratorAccess(user);
    const canBuild = hasSurveyBuilderAccess(user);
    const canViewClosedResults = hasClosedSurveyResultsAccess(user);
    const canReviewRsvpResponses = hasRsvpReviewAccess(user);
    const canUseAuthenticatedSurveyAccess = hasAuthenticatedSurveyAccess(user);
    const canManageSurveys = canModerate;
    const canUseRecipientDirectory = canManageSurveys;
    const canSendTestEmail = hasSurveyEmailTestAccess(user);
    const selectedSurveyClosed = isSurveyExpired(selectedSurvey);
    const selectedSurveyCanOpenWithLogin = canUseAuthenticatedSurveyAccess
        && selectedSurvey.slug !== DEFAULT_SLUG
        && !selectedSurveyClosed
        && isMeetingFormSurveyKind(selectedSurvey.kind);
    const generatedSurveyTitle = buildAttendanceSurveyTitle(builderForm);
    const isBuildingDigitalAttendance = isDigitalAttendanceKind(builderForm.kind);
    const generatedBuilderTitle = isBuildingDigitalAttendance
        ? `Digital Attendance Register - ${generatedSurveyTitle}`
        : generatedSurveyTitle;
    const recipientMemberOptions = useMemo(() => recipientMembers.map((member) => {
        const source = String(member.source || "").startsWith("saved") ? "Saved recipient" : "Member";
        return {
            value: member.email,
            label: `${member.name || member.email} <${member.email}>`,
            source,
        };
    }), [recipientMembers]);
    const groupedRecipientOptions = useMemo(() => {
        const members = recipientMemberOptions.filter((option) => option.source === "Member");
        const saved = recipientMemberOptions.filter((option) => option.source !== "Member");
        return [
            members.length ? { label: "Members", options: members } : null,
            saved.length ? { label: "Saved recipients", options: saved } : null,
        ].filter(Boolean);
    }, [recipientMemberOptions]);
    const formatRecipientOptionLabel = (option, { context } = {}) => (
        <div className="survey-recipient-option">
            <span>{option.label}</span>
            {context === "menu" && option.source && <small>{option.source}</small>}
        </div>
    );
    const selectedSurveySummaryEmails = useMemo(
        () => parseRecipientEmails(
            selectedSurvey.summaryRecipients?.emails
                || selectedSurvey.notificationRecipients?.emails
                || []
        ),
        [selectedSurvey.notificationRecipients, selectedSurvey.summaryRecipients]
    );
    const selectedSurveyInviteEmails = useMemo(
        () => parseRecipientEmails(selectedSurvey.inviteRecipients?.emails || []),
        [selectedSurvey.inviteRecipients]
    );
    const sections = useMemo(() => survey?.definition?.sections || [], [survey]);
    const questions = useMemo(() => sections.flatMap((section) => section.questions || []), [sections]);
    const visibleQuestions = useMemo(
        () => questions.filter((question) => isQuestionVisible(question, answers)),
        [questions, answers]
    );
    const questionsById = useMemo(
        () => Object.fromEntries(questions.map((question) => [question.id, question])),
        [questions]
    );
    const answeredQuestionCount = useMemo(
        () => visibleQuestions.filter((question) => isQuestionAnswered(question, answers)).length,
        [visibleQuestions, answers]
    );
    const missingQuestions = useMemo(
        () => visibleQuestions.filter((question) => isQuestionRequired(question) && !isQuestionAnswered(question, answers)),
        [visibleQuestions, answers]
    );
    const getDisplayQuestionLabel = useCallback(
        (question) => (
            isMeetingFormSurveyKind(selectedSurvey.kind) && question.id === "name"
                ? "Name and surname"
                : isDigitalAttendanceKind(selectedSurvey.kind) && question.id === "masonic_style"
                ? "Title"
                : question.label
        ),
        [selectedSurvey.kind]
    );
    const isSurveyComplete = visibleQuestions.length > 0 && missingQuestions.length === 0;
    const isAuthenticatedSurveyOpen = verifiedAccess?.accessMode === "authenticated" && verifiedAccess?.slug === selectedSurvey.slug;
    const isVerifiedSurveyOpen = Boolean(
        survey
        && verifiedAccess?.slug === selectedSurvey.slug
        && (isAuthenticatedSurveyOpen || verifiedAccess?.token === token)
    );
    const responderName = answers.name || "";
    const responderEmail = answers.email_address || "";
    const responderLodge = answers.lodge_affiliation || "";
    const responderOtherLodge = answers.lodge_affiliation__other || "";

    useEffect(() => {
        setSurvey(null);
        setAnswers({});
        setSelectedAttendancePreference(null);
        setMemberSuggestions([]);
        setResponderMemorySuggestions([]);
        setMemberSuggesting(false);
        setResults(null);
        setResponses([]);
        setPrivateResponses([]);
        setEditingResponseId(null);
        setEditAnswers({});
        setPendingApologyPrompt(null);
        setApologyDraft("");
        setResponderMemoryNotice("");
        setVerifiedAccess(null);
        setStatus("");
        setError("");
        setSubmitting(false);
        lastAutoSubmittedKey.current = "";
    }, [selectedSurvey.slug]);

    useEffect(() => {
        const search = String(answers.name || "").trim();
        if (!isVerifiedSurveyOpen || !isMeetingFormSurveyKind(selectedSurvey.kind) || search.length < 3) {
            setMemberSuggestions([]);
            setResponderMemorySuggestions([]);
            setMemberSuggesting(false);
            return;
        }

        setResponderMemorySuggestions(getResponderMemorySuggestions(search));
        let cancelled = false;
        setMemberSuggesting(true);
        const timer = window.setTimeout(() => {
            axios.post(`/api/surveys/${selectedSurvey.slug}/member-autocomplete`, { token: isAuthenticatedSurveyOpen ? "" : token, search })
                .then(({ data }) => {
                    if (!cancelled) setMemberSuggestions(data || []);
                })
                .catch((err) => {
                    if (!cancelled) {
                        console.error("Could not load member suggestions:", err);
                        setMemberSuggestions([]);
                    }
                })
                .finally(() => {
                    if (!cancelled) setMemberSuggesting(false);
                });
        }, 250);

        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
    }, [answers.name, isAuthenticatedSurveyOpen, isVerifiedSurveyOpen, selectedSurvey.kind, selectedSurvey.slug, token]);

    useEffect(() => {
        if (!isVerifiedSurveyOpen || !isMeetingFormSurveyKind(selectedSurvey.kind)) return;
        const hasResponder = String(responderName).trim() || String(responderEmail).trim();
        const hasLodge = String(responderLodge).trim();
        if (!hasResponder || !hasLodge) return;
        rememberResponderProfile({
            name: responderName,
            email_address: responderEmail,
            lodge_affiliation: responderLodge,
            lodge_affiliation__other: responderOtherLodge,
        });

        const timer = window.setTimeout(() => {
            axios.post(`/api/surveys/${selectedSurvey.slug}/respondent-preference`, {
                token: isAuthenticatedSurveyOpen ? "" : token,
                answers: {
                    rsvp_member_id: answers.rsvp_member_id,
                    name: responderName,
                    email_address: responderEmail,
                    lodge_affiliation: responderLodge,
                    lodge_affiliation__other: responderOtherLodge,
                },
            }).catch((err) => {
                console.error("Could not save RSVP responder preference:", err);
            });
        }, 450);

        return () => window.clearTimeout(timer);
    }, [
        answers.rsvp_member_id,
        isAuthenticatedSurveyOpen,
        isVerifiedSurveyOpen,
        responderEmail,
        responderLodge,
        responderName,
        responderOtherLodge,
        selectedSurvey.kind,
        selectedSurvey.slug,
        token,
    ]);

    useEffect(() => {
        if (!isVerifiedSurveyOpen || !isDigitalAttendanceKind(selectedSurvey.kind)) return;
        setAnswers((current) => (
            current.attendance_role
                ? current
                : pruneHiddenSurveyAnswers({ ...current, attendance_role: "Member of this lodge" }, questions)
        ));
    }, [isVerifiedSurveyOpen, questions, selectedSurvey.kind]);

    useEffect(() => {
        if (!isVerifiedSurveyOpen || !isDigitalAttendanceKind(selectedSurvey.kind)) return;
        const attendanceRole = String(answers.attendance_role || "").trim();
        if (!["Member of this lodge", "Visitor"].includes(attendanceRole)) return;
        const hasResponder = String(responderName).trim() || String(responderEmail).trim();
        const rememberedFields = attendanceRole === "Visitor"
            ? [
                answers.lodge_affiliation,
                answers.lodge_affiliation__other,
                answers.masonic_style,
                answers.masonic_style__other,
                answers.masonic_rank,
                answers.masonic_rank__other,
            ]
            : [
                answers.member_category,
                answers.member_category__other,
                answers.masonic_style,
                answers.masonic_style__other,
                answers.masonic_rank,
                answers.masonic_rank__other,
            ];
        const hasPreference = rememberedFields.some((value) => String(value || "").trim());
        if (!hasResponder || !hasPreference) return;

        const timer = window.setTimeout(() => {
            axios.post(`/api/surveys/${selectedSurvey.slug}/respondent-preference`, {
                token: isAuthenticatedSurveyOpen ? "" : token,
                answers: {
                    rsvp_member_id: answers.rsvp_member_id,
                    name: responderName,
                    email_address: responderEmail,
                    attendance_role: answers.attendance_role,
                    lodge_affiliation: answers.lodge_affiliation,
                    lodge_affiliation__other: answers.lodge_affiliation__other,
                    member_category: answers.member_category,
                    member_category__other: answers.member_category__other,
                    masonic_style: answers.masonic_style,
                    masonic_style__other: answers.masonic_style__other,
                    masonic_rank: answers.masonic_rank,
                    masonic_rank__other: answers.masonic_rank__other,
                },
            }).catch((err) => {
                console.error("Could not save attendance member preference:", err);
            });
        }, 450);

        return () => window.clearTimeout(timer);
    }, [
        answers.attendance_role,
        answers.lodge_affiliation,
        answers.lodge_affiliation__other,
        answers.member_category,
        answers.member_category__other,
        answers.masonic_rank,
        answers.masonic_rank__other,
        answers.masonic_style,
        answers.masonic_style__other,
        answers.rsvp_member_id,
        isAuthenticatedSurveyOpen,
        isVerifiedSurveyOpen,
        responderEmail,
        responderName,
        selectedSurvey.kind,
        selectedSurvey.slug,
        token,
    ]);

    const loadRecipientMembers = useCallback(() => {
        if (!canUseRecipientDirectory) {
            setRecipientMembers([]);
            return Promise.resolve();
        }
        return axios.get("/api/surveys/recipient-members")
            .then(({ data }) => setRecipientMembers(data || []))
            .catch((err) => {
                console.error("Error loading RSVP email recipients:", err);
            });
    }, [canUseRecipientDirectory]);

    useEffect(() => {
        loadRecipientMembers();
    }, [loadRecipientMembers]);

    const getRecipientPanelSummary = (selectedEmails = [], extraEmails = "") => {
        const selectedCount = parseRecipientEmails(selectedEmails).length;
        const extraCount = parseRecipientEmails(extraEmails).length;
        const total = selectedCount + extraCount;
        if (!total) return "No recipients selected yet";
        const parts = [
            selectedCount ? `${selectedCount} saved` : null,
            extraCount ? `${extraCount} typed` : null,
        ].filter(Boolean);
        return `${total} recipient${total === 1 ? "" : "s"} (${parts.join(", ")})`;
    };

    const toggleBuilderRecipientPanel = (panel) => {
        setBuilderRecipientPanels((current) => ({
            ...current,
            [panel]: !current[panel],
        }));
    };

    useEffect(() => {
        if (!canUseRecipientDirectory) {
            setSummaryRecipientMemberEmails([]);
            setInviteRecipientMemberEmails([]);
            return;
        }
        const knownEmails = new Set(recipientMemberOptions.map((option) => option.value));
        setSummaryRecipientMemberEmails(selectedSurveySummaryEmails.filter((email) => knownEmails.has(email)));
        setSummaryRecipientExtraEmails(selectedSurveySummaryEmails.filter((email) => !knownEmails.has(email)).join(", "));
        setInviteRecipientMemberEmails(selectedSurveyInviteEmails.filter((email) => knownEmails.has(email)));
        setInviteRecipientExtraEmails(selectedSurveyInviteEmails.filter((email) => !knownEmails.has(email)).join(", "));
    }, [canUseRecipientDirectory, recipientMemberOptions, selectedSurveyInviteEmails, selectedSurveySummaryEmails]);

    useEffect(() => {
        const cleanToken = String(builderForm.token || "").trim();
        if (!canBuild) {
            setBuilderTokenStatus({ state: "idle", message: "" });
            return;
        }
        if (!cleanToken) {
            setBuilderTokenStatus({ state: "empty", message: "Token required." });
            return;
        }
        if (cleanToken.length < 4) {
            setBuilderTokenStatus({ state: "short", message: "Use at least 4 characters." });
            return;
        }

        let cancelled = false;
        setBuilderTokenStatus({ state: "checking", message: "Checking token..." });
        const timer = window.setTimeout(() => {
            axios.post("/api/surveys/token-availability", { token: cleanToken })
                .then(({ data }) => {
                    if (cancelled) return;
                    setBuilderTokenStatus({
                        state: data.available ? "available" : "taken",
                        message: data.message || (data.available ? "Token available." : "Token already used."),
                    });
                })
                .catch((err) => {
                    if (cancelled) return;
                    console.error("Could not check survey token:", err);
                    setBuilderTokenStatus({ state: "error", message: "Could not check token yet." });
                });
        }, 350);

        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
    }, [builderForm.token, canBuild]);

    const handleTokenChange = (value) => {
        setToken(value);
        setStatus("");
        setError("");
        if (!verifiedAccess || verifiedAccess.slug !== selectedSurvey.slug || (verifiedAccess.accessMode !== "authenticated" && verifiedAccess.token !== value)) {
            setSurvey(null);
            setResults(null);
        }
    };

    const verifyToken = async () => {
        setError("");
        setStatus("");
        const useAuthenticatedAccess = !String(token || "").trim() && canUseAuthenticatedSurveyAccess;
        if (!String(token || "").trim() && !useAuthenticatedAccess && !canModerate) {
            setSurvey(null);
            setResults(null);
            setError("Enter the survey token first.");
            return;
        }
        try {
            const { data } = await axios.post(`/api/surveys/${selectedSurvey.slug}/verify-token`, { token: useAuthenticatedAccess ? "" : token });
            setSurvey(data);
            setVerifiedAccess({
                slug: selectedSurvey.slug,
                token: useAuthenticatedAccess ? "" : token,
                accessMode: data.accessMode || (useAuthenticatedAccess ? "authenticated" : "token"),
            });
            setStatus(useAuthenticatedAccess
                ? (isDigitalAttendanceKind(data.definition?.kind)
                    ? "Member access confirmed. Add your attendance details."
                    : "Member access confirmed. Add your RSVP.")
                : (isDigitalAttendanceKind(data.definition?.kind)
                    ? "Token accepted. Add your attendance details."
                    : "Token accepted. Responses are anonymous."));
            setActiveTab("survey");
        } catch (err) {
            console.error("Survey token failed:", err);
            setError(useAuthenticatedAccess ? "Could not open this form with your login." : "That token was not accepted.");
        }
    };

    const loadSurveyForModerator = async () => {
        if (survey) return survey;
        if (!canModerate && !canUseAuthenticatedSurveyAccess) return null;
        const { data } = await axios.post(`/api/surveys/${selectedSurvey.slug}/verify-token`, {});
        setSurvey(data);
        setVerifiedAccess({ slug: selectedSurvey.slug, token: "", accessMode: data.accessMode || "authenticated" });
        return data;
    };

    useEffect(() => {
        if (!canUseAuthenticatedSurveyAccess) return;
        if (selectedSurvey.slug === DEFAULT_SLUG || selectedSurveyClosed || !isMeetingFormSurveyKind(selectedSurvey.kind)) return;
        if (survey?.slug === selectedSurvey.slug && isAuthenticatedSurveyOpen) return;

        let cancelled = false;
        axios.post(`/api/surveys/${selectedSurvey.slug}/verify-token`, { token: "" })
            .then(({ data }) => {
                if (cancelled) return;
                setSurvey(data);
                setVerifiedAccess({ slug: selectedSurvey.slug, token: "", accessMode: data.accessMode || "authenticated" });
            })
            .catch((err) => {
                if (!cancelled) {
                    console.error("Could not open survey with authenticated access:", err);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [canUseAuthenticatedSurveyAccess, isAuthenticatedSurveyOpen, selectedSurvey.kind, selectedSurvey.slug, selectedSurveyClosed, survey?.slug]);

    const loadResults = useCallback(async () => {
        setError("");
        const canOpenWithoutToken = canUseAuthenticatedSurveyAccess || (selectedSurveyClosed && canViewClosedResults);
        if (!String(token || "").trim() && !canOpenWithoutToken) {
            setResults(null);
            setError(selectedSurveyClosed ? "Log in as a member to view closed survey results." : "Enter the survey token first.");
            return;
        }
        try {
            const { data } = await axios.post(`/api/surveys/${selectedSurvey.slug}/results`, { token: canOpenWithoutToken ? "" : token });
            setResults(data);
            setVerifiedAccess(canOpenWithoutToken ? { slug: selectedSurvey.slug, token: "", accessMode: "authenticated" } : { slug: selectedSurvey.slug, token, accessMode: "token" });
            if (canReviewRsvpResponses && isMeetingFormSurveyKind(data?.kind)) {
                try {
                    const privateResult = await axios.get(`/api/surveys/${selectedSurvey.slug}/private-responses`);
                    setPrivateResponses(privateResult.data || []);
                } catch (privateErr) {
                    console.error("Could not load private RSVP responses:", privateErr);
                }
            } else {
                setPrivateResponses([]);
            }
            setActiveTab("results");
        } catch (err) {
            console.error("Survey results failed:", err);
            setError(canOpenWithoutToken ? "Could not load survey results with your login." : "Could not load survey results with that token.");
        }
    }, [canReviewRsvpResponses, canUseAuthenticatedSurveyAccess, canViewClosedResults, selectedSurvey.slug, selectedSurveyClosed, token]);

    const loadClosedSurveyResults = async (slug) => {
        setSelectedSlug(slug);
        setError("");
        setStatus("");
        if (!canViewClosedResults) {
            setError("Log in as a member to view closed survey results.");
            return;
        }
        try {
            const { data } = await axios.post(`/api/surveys/${slug}/results`, { token: "" });
            setResults(data);
            setVerifiedAccess(null);
            if (canReviewRsvpResponses && isMeetingFormSurveyKind(data?.kind)) {
                try {
                    const privateResult = await axios.get(`/api/surveys/${slug}/private-responses`);
                    setPrivateResponses(privateResult.data || []);
                } catch (privateErr) {
                    console.error("Could not load private RSVP responses:", privateErr);
                }
            } else {
                setPrivateResponses([]);
            }
            setActiveTab("results");
        } catch (err) {
            console.error("Closed survey results failed:", err);
            setError("Could not load closed survey results.");
        }
    };

    const loadLegacyResults = async () => {
        setError("");
        try {
            const { data } = legacySurvey
                ? { data: legacySurvey }
                : await axios.get(`/api/surveys/${DEFAULT_SLUG}/public-results`);
            setResults(data);
            setSelectedSlug(DEFAULT_SLUG);
            setActiveTab("results");
        } catch (err) {
            console.error("Could not load closed survey results:", err);
            setError("Could not load the closed survey analysis.");
        }
    };

    const loadResponses = async () => {
        if (!canModerate) return;
        setError("");
        try {
            await loadSurveyForModerator();
            const { data } = await axios.get(`/api/surveys/${selectedSurvey.slug}/responses`);
            setResponses(data || []);
        } catch (err) {
            console.error("Could not load survey responses:", err);
            const detail = err.response?.data?.error || err.message || "Unknown error";
            const status = err.response?.status ? ` (${err.response.status})` : "";
            setError(`Could not load the response moderation list${status}: ${detail}`);
        }
    };

    const getPrivateReviewSlug = (slugOverride = null) => {
        const fallbackSurvey = allSurveyList.find((item) => item.slug !== DEFAULT_SLUG);
        const candidate = slugOverride
            || (survey?.slug && survey.slug !== DEFAULT_SLUG ? survey.slug : "")
            || (results?.slug && results.slug !== DEFAULT_SLUG ? results.slug : "")
            || (selectedSurvey.slug !== DEFAULT_SLUG ? selectedSurvey.slug : "")
            || fallbackSurvey?.slug
            || "";
        return candidate;
    };

    const loadPrivateResponses = async (slugOverride = null) => {
        if (!canReviewRsvpResponses) return;
        const slug = getPrivateReviewSlug(slugOverride);
        setError("");
        if (!slug || slug === DEFAULT_SLUG) {
            setPrivateResponses([]);
            setActiveTab("private");
            setError("Choose an RSVP forecast or attendance register first.");
            return;
        }
        try {
            setSelectedSlug(slug);
            const { data } = await axios.get(`/api/surveys/${slug}/private-responses`);
            setPrivateResponses(data || []);
            setActiveTab("private");
        } catch (err) {
            console.error("Could not load private survey responses:", err);
            const detail = err.response?.data?.error || err.message || "Unknown error";
            setError(`Could not load the private response list: ${detail}`);
        }
    };

    const sendRsvpTestEmail = async () => {
        if (!canSendTestEmail) return;
        const slug = getPrivateReviewSlug();
        setError("");
        setStatus("");
        if (!slug || slug === DEFAULT_SLUG) {
            setError("Choose an RSVP forecast or attendance register first.");
            return;
        }
        const targetSurvey = allSurveyList.find((item) => item.slug === slug) || selectedSurvey;
        const targetLabel = getMeetingFormNoun(targetSurvey);
        try {
            const notificationEmails = [
                ...summaryRecipientMemberEmails,
                ...parseRecipientEmails(summaryRecipientExtraEmails),
            ];
            const { data } = await axios.post(`/api/surveys/${slug}/send-summary`, {
                dryRun: false,
                testOnly: true,
                notificationEmails,
            });
            const recipients = (data.recipients || []).map((recipient) => recipient.email).filter(Boolean);
            setStatus(recipients.length
                ? `Test summary email sent to ${recipients.join(", ")}.`
                : "Test summary email sent.");
        } catch (err) {
            console.error("Could not send test summary email:", err);
            const detail = err.response?.data?.error || err.message || `Could not send the ${targetLabel} test email.`;
            setError(detail);
        }
    };

    const sendRsvpInviteEmail = async (slugOverride = null, tokenOverride = "") => {
        if (!canBuild) return;
        const slug = getPrivateReviewSlug(slugOverride);
        const tokenToSend = String(tokenOverride || inviteToken || token || "").trim();
        setError("");
        setStatus("");
        if (!slug || slug === DEFAULT_SLUG) {
            setError("Choose an RSVP forecast or attendance register first.");
            return;
        }
        const targetSurvey = allSurveyList.find((item) => item.slug === slug) || selectedSurvey;
        const targetLabel = getMeetingFormNoun(targetSurvey);
        const targetTitle = getMeetingFormTitleCase(targetSurvey);
        if (!tokenToSend) {
            setError(`Enter the ${targetLabel} token before sending the invite email.`);
            return;
        }
        try {
            const notificationEmails = [
                ...inviteRecipientMemberEmails,
                ...parseRecipientEmails(inviteRecipientExtraEmails),
            ];
            const { data } = await axios.post(`/api/surveys/${slug}/send-invite`, {
                token: tokenToSend,
                notificationEmails,
            });
            const recipients = (data.recipients || []).map((recipient) => recipient.email).filter(Boolean);
            setStatus(recipients.length
                ? `${targetTitle} invite email sent to ${recipients.join(", ")}.`
                : `${targetTitle} invite email sent.`);
        } catch (err) {
            console.error("Could not send survey invite email:", err);
            const detail = err.response?.data?.error || err.message || `Could not send the ${targetLabel} invite email.`;
            setError(detail);
        }
    };

    const saveRsvpSummaryRecipients = async () => {
        if (!canManageSurveys) return;
        const slug = getPrivateReviewSlug();
        setError("");
        setStatus("");
        if (!slug || slug === DEFAULT_SLUG) {
            setError("Choose an RSVP forecast or attendance register first.");
            return;
        }
        const notificationEmails = [
            ...summaryRecipientMemberEmails,
            ...parseRecipientEmails(summaryRecipientExtraEmails),
        ];
        try {
            const { data } = await axios.patch(`/api/surveys/${slug}/notification-recipients`, {
                recipientType: "summary",
                notificationEmails,
                includeOfficers: true,
            });
            setSurveyList((current) => current.map((item) =>
                item.slug === slug
                    ? {
                        ...item,
                        notificationRecipients: data.notificationRecipients,
                        summaryRecipients: data.summaryRecipients,
                    }
                    : item
            ));
            await loadRecipientMembers();
            setStatus(notificationEmails.length
                ? `Summary recipients saved: ${notificationEmails.join(", ")}.`
                : "Summary recipients cleared. The app will still try the lodge Master and Secretary.");
        } catch (err) {
            console.error("Could not save summary recipients:", err);
            const detail = err.response?.data?.error || err.message || "Could not save summary recipients.";
            setError(detail);
        }
    };

    const saveRsvpInviteRecipients = async () => {
        if (!canManageSurveys) return;
        const slug = getPrivateReviewSlug();
        setError("");
        setStatus("");
        if (!slug || slug === DEFAULT_SLUG) {
            setError("Choose an RSVP forecast or attendance register first.");
            return;
        }
        const notificationEmails = [
            ...inviteRecipientMemberEmails,
            ...parseRecipientEmails(inviteRecipientExtraEmails),
        ];
        try {
            const { data } = await axios.patch(`/api/surveys/${slug}/notification-recipients`, {
                recipientType: "invite",
                notificationEmails,
                includeOfficers: false,
            });
            setSurveyList((current) => current.map((item) =>
                item.slug === slug
                    ? { ...item, inviteRecipients: data.inviteRecipients }
                    : item
            ));
            await loadRecipientMembers();
            setStatus(notificationEmails.length
                ? `Invite recipients saved: ${notificationEmails.join(", ")}.`
                : "Invite recipients cleared.");
        } catch (err) {
            console.error("Could not save invite recipients:", err);
            const detail = err.response?.data?.error || err.message || "Could not save invite recipients.";
            setError(detail);
        }
    };

    const updateBuilderForm = (field, value) => {
        setBuilderForm((current) => {
            const next = { ...current, [field]: value };
            if (field === "kind") {
                next.title = value === "digital_attendance" ? "Digital Attendance Register" : "Meeting RSVP Forecast";
                next.notificationMemberEmails = [];
                next.notificationEmails = "";
                next.inviteMemberEmails = [];
                next.inviteEmails = "";
                next.inviteImage = null;
            }
            if (field === "meetingDate" && value && !current.expiresAt) {
                const date = new Date(`${value}T00:00:00`);
                if (!Number.isNaN(date.getTime())) {
                    date.setDate(date.getDate() - 2);
                    next.expiresAt = date.toISOString().slice(0, 10);
                }
            }
            return next;
        });
        setCreatedSurvey(null);
        setStatus("");
        setError("");
    };

    const uploadInviteImage = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) return;
        setError("");
        setStatus("");
        if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
            setError("Please choose a JPG, PNG, or WebP image.");
            return;
        }
        if (file.size > 10 * 1024 * 1024) {
            setError("RSVP invite image must be 10 MB or smaller.");
            return;
        }

        const formData = new FormData();
        formData.append("image", file);
        setInviteImageUploading(true);
        try {
            const { data } = await axios.post("/api/surveys/invite-image", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            updateBuilderForm("inviteImage", data.image || null);
            setStatus("RSVP invite image added.");
        } catch (err) {
            console.error("Could not upload RSVP invite image:", err);
            setError(err.response?.data?.error || "Could not upload the RSVP invite image.");
        } finally {
            setInviteImageUploading(false);
        }
    };

    const buildQuickAttendancePayload = () => {
        const selectedSummaryEmails = canUseRecipientDirectory ? parseRecipientEmails(builderForm.notificationMemberEmails || []) : [];
        const manualSummaryEmails = parseRecipientEmails(builderForm.notificationEmails);
        const selectedInviteEmails = canUseRecipientDirectory ? parseRecipientEmails(builderForm.inviteMemberEmails || []) : [];
        const manualInviteEmails = parseRecipientEmails(builderForm.inviteEmails);
        const summaryEmails = [...new Set([...selectedSummaryEmails, ...manualSummaryEmails])];
        const inviteEmails = [...new Set([...selectedInviteEmails, ...manualInviteEmails])];

        return {
            payload: {
                ...builderForm,
                title: generatedBuilderTitle,
                lodge: builderForm.lodge === "__other__"
                    ? String(builderForm.otherLodge || "").trim()
                    : builderForm.lodge,
                notificationMemberEmails: selectedSummaryEmails,
                notificationEmails: summaryEmails,
                inviteMemberEmails: selectedInviteEmails,
                inviteEmails,
                questions: [],
            },
            summaryEmails,
            inviteEmails,
        };
    };

    const createQuickAttendanceSurvey = async (event) => {
        event.preventDefault();
        setError("");
        setStatus("");
        setCreatedSurvey(null);
        setPendingCreate(buildQuickAttendancePayload());
    };

    const confirmQuickAttendanceSurvey = async () => {
        const createRequest = pendingCreate || buildQuickAttendancePayload();
        const isDigitalCreate = isDigitalAttendanceKind(createRequest.payload.kind);
        setError("");
        setStatus("");
        setPendingCreate(null);

        try {
            const endpoint = isDigitalCreate ? "/api/surveys/digital-attendance" : "/api/surveys/quick-attendance";
            const { data } = await axios.post(endpoint, createRequest.payload);
            const createdItem = {
                ...data,
                summaryRecipients: data.summaryRecipients || { includeOfficers: true, emails: createRequest.summaryEmails },
                notificationRecipients: data.notificationRecipients || data.summaryRecipients || { includeOfficers: true, emails: createRequest.summaryEmails },
                inviteRecipients: data.inviteRecipients || { emails: createRequest.inviteEmails },
            };
            setSurveyList((current) => [createdItem, ...current.filter((item) => item.slug !== data.slug)]);
            setSelectedSlug(data.slug);
            setCreatedSurvey({ ...createdItem, token: createRequest.payload.token });
            setToken(createRequest.payload.token);
            setVerifiedAccess(null);
            await loadRecipientMembers();
            if (createRequest.inviteEmails.length) {
                try {
                    await axios.post(`/api/surveys/${data.slug}/send-invite`, {
                        token: createRequest.payload.token,
                        notificationEmails: createRequest.inviteEmails,
                    });
                    setStatus(isDigitalCreate
                        ? `Attendance register created and invite emailed to ${createRequest.inviteEmails.join(", ")}.`
                        : `Survey created and RSVP invite emailed to ${createRequest.inviteEmails.join(", ")}.`);
                } catch (mailErr) {
                    console.error("Survey created, but invite email failed:", mailErr);
                    const detail = mailErr.response?.data?.error || "Invite email could not be sent.";
                    setStatus(isDigitalCreate
                        ? "Attendance register created, but the invite email did not send."
                        : "Survey created, but the RSVP invite email did not send.");
                    setError(detail);
                }
            } else if (isDigitalCreate) {
                setStatus("Digital attendance register created. Share the link and token beside the physical register.");
            } else {
                setStatus("Survey created. Add invite emails if you want the app to send the RSVP link and token.");
            }
            setActiveTab("survey");
        } catch (err) {
            console.error("Could not create survey:", err);
            const detail = err.response?.data?.error || "Could not create that survey.";
            setError(detail);
        }
    };

    const removeSurveyFromList = (slug) => {
        setSurveyList((current) => current.filter((item) => item.slug !== slug));
        if (selectedSurvey.slug === slug) {
            setSelectedSlug(DEFAULT_SLUG);
            setActiveTab("hub");
        }
    };

    const saveClosingDate = async (event) => {
        event.preventDefault();
        setSavingClosingDate(true);
        setError("");
        try {
            const { data } = await axios.patch(`/api/surveys/${closingDateEditor.slug}/closing-date`, { expiresAt: closingDateEditor.date });
            const original = [...surveyList, ...archivedSurveyList].find(item => item.slug === closingDateEditor.slug);
            setSurveyList(current => [{ ...original, ...data.survey, archiveState: undefined }, ...current.filter(item => item.slug !== data.survey.slug)]);
            setArchivedSurveyList(current => current.filter(item => item.slug !== data.survey.slug));
            setClosingDateEditor(null);
            setStatus("Closing date updated. The form is open until the end of that day (South African time). Existing responses are retained.");
        } catch (err) {
            setError(err.response?.data?.error || "Could not change the closing date.");
        } finally {
            setSavingClosingDate(false);
        }
    };

    const archiveSurvey = async (event, slug) => {
        event.stopPropagation();
        setError("");
        setStatus("");
        try {
            await axios.patch(`/api/surveys/${slug}/archive`);
            const archivedSurvey = surveyList.find((item) => item.slug === slug);
            if (archivedSurvey) {
                setArchivedSurveyList((current) => [
                    { ...archivedSurvey, is_active: false },
                    ...current.filter((item) => item.slug !== slug),
                ]);
            }
            removeSurveyFromList(slug);
            setStatus("Survey archived and moved to Archived RSVPs.");
        } catch (err) {
            console.error("Could not archive survey:", err);
            setError(err.response?.data?.error || "Could not archive that survey.");
        }
    };

    const deleteSurvey = async (event, slug, title) => {
        event.stopPropagation();
        if (!window.confirm(`Delete "${title}" and all its responses? This cannot be undone.`)) return;
        setError("");
        setStatus("");
        try {
            await axios.delete(`/api/surveys/${slug}`);
            removeSurveyFromList(slug);
            setStatus("Survey deleted.");
        } catch (err) {
            console.error("Could not delete survey:", err);
            setError(err.response?.data?.error || "Could not delete that survey.");
        }
    };

    const deleteResponse = async (id) => {
        if (!window.confirm("Delete this anonymous survey response?")) return;
        try {
            await axios.delete(`/api/surveys/${selectedSurvey.slug}/responses/${id}`);
            if (token) {
                await Promise.all([loadResults(), loadResponses()]);
            } else {
                await loadResponses();
            }
        } catch (err) {
            console.error("Could not delete survey response:", err);
            setError("Could not delete that response.");
        }
    };

    const saveResponseEdit = async () => {
        if (!editingResponseId) return;
        setError("");
        try {
            await axios.put(`/api/surveys/${selectedSurvey.slug}/responses/${editingResponseId}`, { answers: editAnswers });
            setStatus("Survey response updated.");
            setEditingResponseId(null);
            setEditAnswers({});
            if (token) {
                await Promise.all([loadResults(), loadResponses()]);
            } else {
                await loadResponses();
            }
        } catch (err) {
            console.error("Could not update survey response:", err);
            setError("Could not update that response.");
        }
    };

    const applyMemberSuggestion = (member) => {
        const memberLodges = Array.isArray(member.lodges)
            ? member.lodges.filter(Boolean)
            : [member.lodge].filter(Boolean);
        const preferredLodge = String(member.preferredLodge || "").trim();
        const preferredOtherLodge = String(member.preferredOtherLodge || "").trim();
        const nextLodge = isDigitalAttendanceKind(selectedSurvey.kind)
            ? ""
            : preferredLodge || (memberLodges.length === 1 ? memberLodges[0] : "");
        const attendancePreference = member.attendancePreference || null;
        setAnswers((current) => {
            const targetAttendanceRole = isDigitalAttendanceKind(selectedSurvey.kind)
                ? current.attendance_role || "Member of this lodge"
                : current.attendance_role;
            return pruneHiddenSurveyAnswers(applyAttendancePreferenceToAnswers({
                ...current,
                rsvp_member_id: member.id,
                rsvp_member_lodges: memberLodges,
                name: member.name || current.name || "",
                email_address: member.email || current.email_address || "",
                attendance_role: isDigitalAttendanceKind(selectedSurvey.kind)
                    ? targetAttendanceRole
                    : current.attendance_role,
                lodge_affiliation: nextLodge || (
                    memberLodges.includes(current.lodge_affiliation) ? current.lodge_affiliation : ""
                ),
                lodge_affiliation__other: preferredOtherLodge || current.lodge_affiliation__other || "",
            }, attendancePreference, targetAttendanceRole), questions);
        });
        setSelectedAttendancePreference(hasAnyAttendancePreferenceValues(attendancePreference) ? attendancePreference : null);
        rememberResponderProfile({
            name: member.name || "",
            email_address: member.email || "",
            lodge_affiliation: nextLodge,
            lodge_affiliation__other: preferredOtherLodge,
        });
        if (preferredLodge) setResponderMemoryNotice("We selected the lodge this person used last time. Please check it is correct.");
        if (hasAttendancePreferenceValues(attendancePreference, "Member of this lodge")) setResponderMemoryNotice("We selected this person's last member attendance details for this lodge. Please check they are correct.");
        setMemberSuggestions([]);
    };

    const applyResponderMemorySuggestion = (profile) => {
        setAnswers((current) => (
            pruneHiddenSurveyAnswers({
                ...current,
                name: profile.name || current.name || "",
                email_address: profile.email || current.email_address || "",
                lodge_affiliation: profile.lodge || current.lodge_affiliation || "",
                lodge_affiliation__other: profile.otherLodge || current.lodge_affiliation__other || "",
            }, questions)
        ));
        setResponderMemoryNotice("We filled in that saved responder. Please check the details are still correct.");
        setResponderMemorySuggestions([]);
        setMemberSuggestions([]);
    };

    const beginResponseEdit = (response) => {
        const nextAnswers = { ...(response.answers || {}) };
        if (looksLikeUuid(nextAnswers.member_category)) {
            if (!nextAnswers.rsvp_member_id) nextAnswers.rsvp_member_id = nextAnswers.member_category;
            nextAnswers.member_category = "Member";
        }
        if (nextAnswers.member_category === "Officer") {
            nextAnswers.member_category = "Member";
            nextAnswers.member_acting_officer = true;
            nextAnswers.officer_role = nextAnswers.officer_role || "Office holder";
        }
        if (typeof nextAnswers.visitor_acting_officer === "boolean") {
            nextAnswers.visitor_acting_officer = nextAnswers.visitor_acting_officer ? "Yes" : "No";
        }
        setEditingResponseId(response.id);
        setEditAnswers(nextAnswers);
        setStatus("");
        setError("");
    };

    const updateAnswerFor = (setter, question, value, checked = null) => {
        if (setter === setAnswers && question.id === "name") {
            setSelectedAttendancePreference(null);
        }
        if (setter === setAnswers
            && question.id === "attendance_role"
            && ["Member of this lodge", "Visitor"].includes(value)
            && hasAttendancePreferenceValues(selectedAttendancePreference, value)) {
            setResponderMemoryNotice(`We selected this person's last ${value === "Visitor" ? "visitor" : "member"} attendance details for this lodge. Please check they are correct.`);
        }
        setter((current) => {
            if (question.type === "multi") {
                const existing = Array.isArray(current[question.id]) ? current[question.id] : [];
                const initialNext = checked
                    ? [...existing, value]
                    : existing.filter((item) => item !== value);
                const next = applyMultiChoiceRules(question, initialNext, value, checked);
                const updated = { ...current, [question.id]: question.max ? next.slice(0, question.max) : next };
                if (isOtherSurveyOption(value) && !checked) delete updated[`${question.id}__other`];
                if (!next.some(isOtherSurveyOption)) delete updated[`${question.id}__other`];
                return pruneHiddenSurveyAnswers(updated, questions);
            }

            const updated = { ...current, [question.id]: value };
            if (setter === setAnswers && question.id === "name" && value !== current.name) {
                delete updated.rsvp_member_id;
                delete updated.rsvp_member_lodges;
            }
            if (setter === setAnswers
                && question.id === "attendance_role"
                && ["Member of this lodge", "Visitor"].includes(value)
                && hasAttendancePreferenceValues(selectedAttendancePreference, value)) {
                Object.assign(updated, applyAttendancePreferenceToAnswers(updated, selectedAttendancePreference, value));
            }
            if (setter === setAnswers && question.id === "attendance_role" && value !== "Visitor") {
                updated.lodge_affiliation = "";
            }
            if (!isOtherSurveyOption(value)) delete updated[`${question.id}__other`];
            return pruneHiddenSurveyAnswers(updated, questions);
        });
    };

    const updateMatrixAnswerFor = (setter, questionId, prompt, value) => {
        setter((current) => ({
            ...current,
            [questionId]: {
                ...(current[questionId] || {}),
                [prompt]: value,
            },
        }));
    };

    const updateOtherAnswerFor = (setter, question, value) => {
        setter((current) => ({
            ...current,
            [`${question.id}__other`]: value,
        }));
    };

    const submitSurvey = useCallback(async (event, {
        automated = false,
        submissionKey = "",
        skipApologyPrompt = false,
        answersOverride = null,
    } = {}) => {
        event?.preventDefault();
        if (submitting) return;
        const answersToSubmit = answersOverride || answers;
        setError("");
        setStatus(automated ? "All answers complete. Submitting your RSVP..." : "");

        if (!isSurveyComplete) {
            const firstMissing = missingQuestions[0] ? getDisplayQuestionLabel(missingQuestions[0]) : "all required questions";
            if (automated) lastAutoSubmittedKey.current = "";
            setError(`Please complete all questions before submitting. First missing: ${firstMissing}`);
            return;
        }

        if (isMeetingFormSurveyKind(selectedSurvey.kind) && String(answersToSubmit.name || "").trim().split(/\s+/).length < 2) {
            if (automated) lastAutoSubmittedKey.current = "";
            setError("Please enter your name and surname.");
            return;
        }

        if (isRsvpSurveyKind(selectedSurvey.kind)
            && String(answersToSubmit.attendance_status || "").trim() === "No"
            && !String(answersToSubmit.apology_reason || "").trim()
            && !skipApologyPrompt) {
            setStatus("");
            setApologyDraft("");
            setPendingApologyPrompt({ automated, submissionKey });
            if (automated) lastAutoSubmittedKey.current = submissionKey;
            return;
        }

        setSubmitting(true);
        try {
            const response = await axios.post(`/api/surveys/${selectedSurvey.slug}/responses`, {
                token: isAuthenticatedSurveyOpen ? "" : token,
                answers: answersToSubmit,
            });
            if (automated) lastAutoSubmittedKey.current = submissionKey;
            if (isRsvpSurveyKind(selectedSurvey.kind)) {
                rememberResponderProfile(answersToSubmit);
                setStatus(response.data?.updated
                    ? "Thank you. Your RSVP has been updated."
                    : "Thank you. Your RSVP has been submitted.");
            } else if (isDigitalAttendanceKind(selectedSurvey.kind)) {
                rememberResponderProfile(answersToSubmit);
                setStatus(response.data?.updated
                    ? "Thank you. Your attendance has been updated."
                    : "Thank you. Your attendance has been recorded.");
            } else {
                setStatus("Thank you. Your anonymous response has been submitted.");
                setAnswers({});
            }
            await loadResults();
        } catch (err) {
            console.error("Survey submit failed:", err);
            if (automated) lastAutoSubmittedKey.current = "";
            const backendMessage = err.response?.data?.error;
            if (backendMessage) {
                setError(backendMessage);
            } else if (err.response?.status === 403) {
                setError("Could not submit the survey. Please check the token and try again.");
            } else {
                setError("Could not submit the survey. Please try again, or ask the Secretary to check the form setup.");
            }
        } finally {
            setSubmitting(false);
        }
    }, [answers, getDisplayQuestionLabel, isAuthenticatedSurveyOpen, isSurveyComplete, loadResults, missingQuestions, selectedSurvey.kind, selectedSurvey.slug, submitting, token]);

    const renderQuestion = (question, answerBag = answers, answerSetter = setAnswers, namePrefix = "survey") => {
        const options = getOptions(question, answerBag, questionsById);
        const value = answerBag[question.id];

        if (question.type === "checkbox") {
            return (
                <label className="survey-check single">
                    <input
                        type="checkbox"
                        checked={Boolean(value)}
                        onChange={(event) => updateAnswerFor(answerSetter, question, event.target.checked)}
                    />
                    <span>{question.checkboxLabel || question.label}</span>
                </label>
            );
        }

        if (question.type === "multi") {
            const selected = Array.isArray(value) ? value : [];
            return (
                <>
                    <div className="survey-option-grid">
                        {options.map((option) => (
                            <label key={option} className="survey-check">
                                <input
                                    type="checkbox"
                                    checked={selected.includes(option)}
                                    disabled={Boolean(question.max && selected.length >= question.max && !selected.includes(option))}
                                    onChange={(event) => updateAnswerFor(answerSetter, question, option, event.target.checked)}
                                />
                                <span>{option}</span>
                            </label>
                        ))}
                    </div>
                    {allowsOtherText(question) && selected.some(isOtherSurveyOption) && (
                        <input
                            className="survey-other-input"
                            type="text"
                            value={answerBag[`${question.id}__other`] || ""}
                            onChange={(event) => updateOtherAnswerFor(answerSetter, question, event.target.value)}
                            placeholder="Please specify"
                        />
                    )}
                </>
            );
        }

        if (question.type === "single") {
            if (question.inputControl === "select" || options.length > 8) {
                const selectedOption = options
                    .map((option) => ({ value: option, label: option }))
                    .find((option) => option.value === value) || null;
                const memberLodgeChoices = question.id === "lodge_affiliation" && answerSetter === setAnswers
                    ? (Array.isArray(answerBag.rsvp_member_lodges) ? answerBag.rsvp_member_lodges.filter(Boolean) : [])
                    : [];

                return (
                    <>
                        {memberLodgeChoices.length > 1 && (
                            <div className="survey-member-lodge-options">
                                <span>You appear linked to multiple lodges. Choose the lodge you are attending from.</span>
                                <div>
                                    {memberLodgeChoices.map((lodge) => (
                                        <button
                                            type="button"
                                            key={lodge}
                                            className={value === lodge ? "selected" : ""}
                                            onClick={() => updateAnswerFor(answerSetter, question, lodge)}
                                        >
                                            {lodge}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                        {memberLodgeChoices.length === 1 && (
                            <p className="survey-field-note">Filled from the member record. You can change it if needed.</p>
                        )}
                        <Select
                            classNamePrefix="survey-select"
                            value={selectedOption}
                            onChange={(option) => updateAnswerFor(answerSetter, question, option?.value || "")}
                            options={options.map((option) => ({ value: option, label: option }))}
                            placeholder="Select an option"
                            isClearable
                            menuPortalTarget={getSelectPortalTarget()}
                            menuPosition="fixed"
                        />
                        {question.id === "lodge_affiliation" && allowsOtherText(question) && !isOtherSurveyOption(value) && (
                            <button
                                type="button"
                                className="survey-other-lodge-button"
                                onClick={() => updateAnswerFor(answerSetter, question, "Other")}
                            >
                                My lodge/body is not listed
                            </button>
                        )}
                        {allowsOtherText(question) && isOtherSurveyOption(value) && (
                            <div className={question.id === "lodge_affiliation" ? "survey-other-lodge-panel" : ""}>
                                {question.id === "lodge_affiliation" && (
                                    <span>Enter the full lodge/body name, including number and constitution/order if known.</span>
                                )}
                                <input
                                    className="survey-other-input"
                                    type="text"
                                    value={answerBag[`${question.id}__other`] || ""}
                                    onChange={(event) => updateOtherAnswerFor(answerSetter, question, event.target.value)}
                                    placeholder={question.id === "lodge_affiliation" ? "e.g. Lodge Name No. 123 S.C." : "Please specify"}
                                />
                            </div>
                        )}
                    </>
                );
            }

            return (
                <>
                    <div className="survey-option-grid compact">
                        {options.map((option) => (
                            <label key={option} className="survey-radio">
                                <input
                                    type="radio"
                                    name={`${namePrefix}-${question.id}`}
                                    checked={value === option}
                                    onChange={() => updateAnswerFor(answerSetter, question, option)}
                                />
                                <span>{option}</span>
                            </label>
                        ))}
                    </div>
                    {allowsOtherText(question) && isOtherSurveyOption(value) && (
                        <input
                            className="survey-other-input"
                            type="text"
                            value={answerBag[`${question.id}__other`] || ""}
                            onChange={(event) => updateOtherAnswerFor(answerSetter, question, event.target.value)}
                            placeholder={question.id === "lodge_affiliation" ? "e.g. Lodge Name No. 123 S.C." : "Please specify"}
                        />
                    )}
                </>
            );
        }

        if (question.type === "matrix") {
            return (
                <div className="survey-matrix">
                    {(question.prompts || []).map((prompt) => (
                        <div className="survey-matrix-row" key={prompt}>
                            <strong>{prompt}</strong>
                            <div>
                                {(question.scale || []).map((scale) => (
                                    <label key={scale}>
                                        <input
                                            type="radio"
                                            name={`${namePrefix}-${question.id}-${prompt}`}
                                            checked={value?.[prompt] === scale}
                                            onChange={() => updateMatrixAnswerFor(answerSetter, question.id, prompt, scale)}
                                        />
                                        <span>{scale}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            );
        }

        if (question.type === "textarea" || question.multiline) {
            return (
                <textarea
                    rows={5}
                    value={value || ""}
                    onChange={(event) => updateAnswerFor(answerSetter, question, event.target.value)}
                />
            );
        }

        const showMemberAutocomplete = question.id === "name" && answerSetter === setAnswers && isMeetingFormSurveyKind(selectedSurvey.kind);

        return (
            <>
                <input
                    type={question.inputControl === "email" ? "email" : "text"}
                    value={value || ""}
                    onChange={(event) => updateAnswerFor(answerSetter, question, event.target.value)}
                    placeholder={question.id === "name" ? "First name and surname" : undefined}
                    autoComplete={showMemberAutocomplete ? "off" : question.inputControl === "email" ? "email" : undefined}
                />
                {showMemberAutocomplete && String(value || "").trim().length > 0 && String(value || "").trim().split(/\s+/).length < 2 && (
                    <p className="survey-field-note">Please enter both first name and surname.</p>
                )}
                {showMemberAutocomplete && (memberSuggesting || responderMemorySuggestions.length > 0 || memberSuggestions.length > 0) && (
                    <div className="survey-member-suggestions">
                        {memberSuggesting && <span className="survey-member-suggestion-status">Looking for matching members...</span>}
                        {responderMemorySuggestions
                            .filter((profile) => !memberSuggestions.some((member) => responderProfileMatchesMember(profile, member)))
                            .map((profile) => {
                            const lodgeLabel = getResponderProfileLodgeLabel(profile);
                            const key = getResponderMemoryKeys({ name: profile.name, email: profile.email })[0]
                                || normalizeMemoryKeyPart(`${profile.name}|${profile.email}|${profile.lodge}`);
                            return (
                                <button type="button" key={`memory-${key}`} onClick={() => applyResponderMemorySuggestion(profile)}>
                                    <strong>{profile.name || profile.email}</strong>
                                    <span>{[lodgeLabel, profile.email, "Saved on this device"].filter(Boolean).join(" · ")}</span>
                                </button>
                            );
                        })}
                        {memberSuggestions.map((member) => (
                            (() => {
                                const rememberedProfile = responderMemorySuggestions.find((profile) => responderProfileMatchesMember(profile, member));
                                const mergedMember = rememberedProfile
                                    ? {
                                        ...member,
                                        preferredLodge: member.preferredLodge || rememberedProfile.lodge || "",
                                        preferredOtherLodge: member.preferredOtherLodge || rememberedProfile.otherLodge || "",
                                    }
                                    : member;
                                const memberLodges = Array.isArray(member.lodges)
                                    ? member.lodges.filter(Boolean)
                                    : [member.lodge].filter(Boolean);
                                const rememberedLodge = getResponderProfileLodgeLabel(rememberedProfile);
                                const backendPreferredLodge = getResponderProfileLodgeLabel({
                                    lodge: member.preferredLodge,
                                    otherLodge: member.preferredOtherLodge,
                                });
                                const attendancePreference = member.attendancePreference || {};
                                const activeAttendanceRole = String(answers.attendance_role || "").trim();
                                const activePreference = getAttendancePreferenceForRole(attendancePreference, activeAttendanceRole);
                                const fallbackPreference = getAttendancePreferenceForRole(attendancePreference, "Member of this lodge");
                                const labelPreference = hasAttendancePreferenceValues(attendancePreference, activeAttendanceRole)
                                    ? activePreference
                                    : fallbackPreference;
                                const attendanceLabel = hasAnyAttendancePreferenceValues(attendancePreference)
                                    ? [
                                        labelPreference.member_category,
                                        labelPreference.lodge_affiliation,
                                        labelPreference.masonic_style,
                                        labelPreference.masonic_rank,
                                    ].filter(Boolean).join(" · ")
                                    : "";
                                const lodgeLabel = isDigitalAttendanceKind(selectedSurvey.kind) && attendanceLabel
                                    ? `${attendanceLabel} · Last attendance here`
                                    : rememberedLodge
                                    ? `${rememberedLodge} · Saved on this device`
                                    : backendPreferredLodge
                                    ? `${backendPreferredLodge} · Last RSVP lodge`
                                    : memberLodges.length > 1
                                    ? `${memberLodges.length} lodges: ${memberLodges.join(", ")}`
                                    : memberLodges[0];
                                return (
                                    <button type="button" key={member.id} onClick={() => applyMemberSuggestion(mergedMember)}>
                                        <strong>{member.name}</strong>
                                        <span>{[lodgeLabel, member.email].filter(Boolean).join(" · ") || "Member record"}</span>
                                    </button>
                                );
                            })()
                        ))}
                    </div>
                )}
            </>
        );
    };

    const resultQuestions = results?.questions || [];
    const attendanceResultsActive = activeTab === "results" && isMeetingAttendanceResults(results, selectedSurvey);
    const chartQuestions = resultQuestions.filter((question) =>
        ["single", "multi"].includes(question.type) && Object.keys(question.counts || {}).length
    );
    const matrixQuestions = resultQuestions.filter((question) => question.type === "matrix");
    const textQuestions = resultQuestions.filter((question) => question.textResponses?.length);
    const beforeCharts = chartQuestions.filter((question) => [
        "motivation_joining",
        "understanding_before_joining"
    ].includes(question.id));
    const coreCharts = chartQuestions.filter((question) => [
        "current_activity",
        "overall_experience",
        "recommend_to_friend",
        "connected_to_lodge",
        "engagement_five_years"
    ].includes(question.id));
    const currentDetailCharts = chartQuestions.filter((question) => [
        "rewarding_aspects"
    ].includes(question.id));
    const barrierCharts = chartQuestions.filter((question) => [
        "participation_barriers"
    ].includes(question.id));
    const practicalBarrierCharts = chartQuestions.filter((question) => [
        "own_lodge_travel_burden",
        "visiting_lodge_burden",
        "festive_board_driving",
        "contacted_after_absence"
    ].includes(question.id));
    const backgroundCharts = chartQuestions.filter((question) => [
        "age_band",
        "age_at_joining",
        "masonic_rank",
        "masonic_degree_status",
        "current_lodge_roles",
        "membership_scope"
    ].includes(question.id));
    const otherCharts = chartQuestions.filter((question) =>
        ![...beforeCharts, ...coreCharts, ...currentDetailCharts, ...barrierCharts, ...practicalBarrierCharts, ...backgroundCharts].some((item) => item.id === question.id)
    );
    const recommendQuestion = resultQuestions.find((question) => question.id === "recommend_to_friend");
    const recommendCount = sumCounts(recommendQuestion, ["Definitely", "Probably"]);
    const privateAttendanceCounts = countPrivateResponsesBy(privateResponses, "attendance");
    const privateLodgeCounts = countPrivateResponsesBy(privateResponses, "lodge");
    const surveyOptions = surveyList.map((item) => ({
        value: item.slug,
        label: item.title,
        survey: item,
    }));
    const rsvpSurveyOptions = surveyOptions.filter((option) => option.value !== DEFAULT_SLUG);
    const searchableSurveys = useMemo(
        () => [
            ...surveyList.map((item) => ({ ...item, archiveState: "current" })),
            ...(canReviewRsvpResponses && surveyFilters.includeArchived
                ? archivedSurveyList.map((item) => ({ ...item, archiveState: "archived" }))
                : []),
        ].filter((item) => item.slug !== DEFAULT_SLUG),
        [archivedSurveyList, canReviewRsvpResponses, surveyFilters.includeArchived, surveyList]
    );
    const surveyLodgeFilterOptions = useMemo(() => {
        const byLodge = new Map();
        searchableSurveys.forEach((item) => {
            const lodge = String(item.meeting?.lodge || "").trim();
            if (!lodge || byLodge.has(lodge)) return;
            byLodge.set(lodge, { value: lodge, label: lodge });
        });
        return [{ value: "", label: "Show all lodges" }, ...Array.from(byLodge.values()).sort((a, b) => a.label.localeCompare(b.label))];
    }, [searchableSurveys]);
    const surveyKindFilterOptions = useMemo(
        () => [
            { value: "all", label: "All form types" },
            { value: "meeting_attendance", label: "RSVP forecasts" },
            { value: "digital_attendance", label: "Attendance registers" },
        ],
        []
    );
    const filteredSurveyList = useMemo(() => searchableSurveys.filter((item) => {
        if (permanentLodgeId && (!item.is_active || (item.expires_at && new Date(item.expires_at) <= new Date()))) return false;
        if (surveyFilters.lodge && String(item.meeting?.lodge || "") !== surveyFilters.lodge) return false;
        if (surveyFilters.kind !== "all" && item.kind !== surveyFilters.kind) return false;
        return true;
    }), [searchableSurveys, surveyFilters.kind, surveyFilters.lodge, permanentLodgeId]);
    const showClosedResultsTab = selectedSurveyClosed && canViewClosedResults && selectedSurvey.slug !== DEFAULT_SLUG;
    const submitApologyPrompt = (includeNote) => {
        const prompt = pendingApologyPrompt || { automated: false, submissionKey: "" };
        const nextAnswers = includeNote
            ? { ...answers, apology_reason: apologyDraft }
            : answers;
        if (includeNote) {
            setAnswers(nextAnswers);
        }
        setPendingApologyPrompt(null);
        setApologyDraft("");
        submitSurvey(null, {
            automated: prompt.automated,
            submissionKey: prompt.submissionKey,
            skipApologyPrompt: true,
            answersOverride: nextAnswers,
        });
    };

    return (
        <>
        <main className="survey-page">
            <section className="survey-hero">
                <div>
                    <p className="survey-eyebrow">Survey Hub</p>
                    <h1>
                        {activeTab === "survey"
                            ? survey?.title || selectedSurvey.title || "Masonic Membership Survey"
                            : attendanceResultsActive
                                ? results.title || selectedSurvey.title || "Meeting RSVP Forecast"
                            : activeTab === "results" && results?.slug === DEFAULT_SLUG
                                ? results.title
                                : "Masonic Surveys"}
                    </h1>
                    <p>
                        {activeTab === "survey"
                            ? survey?.description || selectedSurvey.description || "Enter the survey token to participate and view anonymous aggregate results."
                            : attendanceResultsActive
                                ? results.description || "Anonymous RSVP totals for this token-protected planning survey."
                            : activeTab === "results" && results?.slug === DEFAULT_SLUG
                                ? "Public summary of the anonymous membership, engagement, and retention survey. Submissions are closed; the results remain available for research and lodge planning."
                            : "Open RSVPs and attendance registers with your member login, or use a meeting token when browsing publicly."}
                    </p>
                </div>
                <div className="survey-token-card">
                    {activeTab === "hub" ? (
                        <>
                            <strong className="survey-tool-title">Survey workspace</strong>
                            <p className="survey-muted">
                                The old survey analysis is public. Current RSVPs and registers open automatically for logged-in members, with tokens kept for public access.
                            </p>
                            {surveyList.length > 0 && (
                                <label>
                                    Open a current survey
                                    <Select
                                        classNamePrefix="survey-select"
                                        value={surveyOptions.find((option) => option.value === selectedSurvey.slug) || null}
                                        onChange={(option) => setSelectedSlug(option?.value || DEFAULT_SLUG)}
                                        options={surveyOptions}
                                        placeholder="Choose a survey"
                                        isSearchable
                                        menuPortalTarget={getSelectPortalTarget()}
                                        menuPosition="fixed"
                                    />
                                </label>
                            )}
                            {selectedSurvey.slug !== DEFAULT_SLUG && !isSurveyExpired(selectedSurvey) && selectedSurveyCanOpenWithLogin && (
                                <div className="survey-token-required">
                                    <span>Member access</span>
                                    <strong>{selectedSurvey.meeting?.lodge || selectedSurvey.title || "Selected survey"}</strong>
                                    <small>
                                        {isDigitalAttendanceKind(selectedSurvey.kind)
                                            ? "You are logged in, so no token is needed to open this attendance register."
                                            : "You are logged in, so no token is needed to open this RSVP."}
                                    </small>
                                    <button type="button" onClick={verifyToken}>
                                        {isDigitalAttendanceKind(selectedSurvey.kind) ? "Open Register" : "Open RSVP"}
                                    </button>
                                </div>
                            )}
                            {selectedSurvey.slug !== DEFAULT_SLUG && !isSurveyExpired(selectedSurvey) && !selectedSurveyCanOpenWithLogin && (
                                <div className="survey-token-required">
                                    <span>Token required</span>
                                    <strong>{selectedSurvey.meeting?.lodge || selectedSurvey.title || "Selected survey"}</strong>
                                    <small>
                                        {isDigitalAttendanceKind(selectedSurvey.kind)
                                            ? "Enter the token shown beside the physical register to open this attendance form."
                                            : "Enter the token from the survey link or invitation to open this RSVP."}
                                    </small>
                                    <div className="survey-token-entry compact">
                                        <input
                                            id="survey-token-hub"
                                            type="text"
                                            value={token}
                                            onChange={(event) => handleTokenChange(event.target.value)}
                                            placeholder="Survey token"
                                        />
                                        <button type="button" onClick={verifyToken}>Open</button>
                                    </div>
                                </div>
                            )}
                            <button type="button" className="secondary" onClick={() => setActiveTab("survey")}>
                                Use Full Token Form
                            </button>
                            {canReviewRsvpResponses && (
                                <button type="button" className="secondary" onClick={loadPrivateResponses}>
                                    RSVP Review
                                </button>
                            )}
                        </>
                    ) : (
                        <>
                            {surveyList.length > 1 && (
                                <label>
                                    Choose survey
                                    <select
                                        value={selectedSurvey.slug}
                                        onChange={(event) => setSelectedSlug(event.target.value)}
                                    >
                                        {surveyList.map((item) => (
                                            <option key={item.slug} value={item.slug}>
                                                {item.title}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                            )}
                            {selectedSurveyCanOpenWithLogin ? (
                                <div className="survey-token-required">
                                    <span>Member access</span>
                                    <strong>No token needed while you are logged in.</strong>
                                    <button type="button" onClick={verifyToken}>
                                        {isDigitalAttendanceKind(selectedSurvey.kind) ? "Open Register" : "Open RSVP"}
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <label htmlFor="survey-token" className="survey-token-entry-label">
                                        Survey token
                                        <span>Paste the meeting token here, for example 392IC.</span>
                                    </label>
                                    <div className="survey-token-entry">
                                        <input
                                            id="survey-token"
                                            type="text"
                                            value={token}
                                            onChange={(event) => handleTokenChange(event.target.value)}
                                            placeholder="Enter token"
                                        />
                                        <button type="button" onClick={verifyToken}>
                                            {isDigitalAttendanceKind(selectedSurvey.kind) ? "Open Register" : "Open Survey"}
                                        </button>
                                    </div>
                                </>
                            )}
                            <button type="button" className="secondary" onClick={loadResults}>
                                View Results
                            </button>
                            {canBuild && (
                                <button type="button" className="secondary" onClick={() => setActiveTab("builder")}>
                                    Build Quick Survey
                                </button>
                            )}
                        </>
                    )}
                </div>
            </section>

            {error && <div className="survey-alert error">{error}</div>}
            {status && <div className="survey-alert success">{status}</div>}

            {(isVerifiedSurveyOpen || canModerate || canBuild) && (
                <div className="survey-tabs">
                    <button className={activeTab === "hub" ? "active" : ""} onClick={() => setActiveTab("hub")}>
                        Survey Hub
                    </button>
                    {(isVerifiedSurveyOpen || showClosedResultsTab) && (
                        <>
                            {isVerifiedSurveyOpen && (
                                <button className={activeTab === "survey" ? "active" : ""} onClick={() => setActiveTab("survey")}>Survey</button>
                            )}
                            <button className={activeTab === "results" ? "active" : ""} onClick={loadResults}>Results</button>
                        </>
                    )}
	                    {canModerate && (
	                        <button className={activeTab === "moderate" ? "active" : ""} onClick={() => {
	                            setActiveTab("moderate");
	                            loadResponses();
	                        }}>
	                            Moderate
	                        </button>
	                    )}
                    {canReviewRsvpResponses && isMeetingFormSurveyKind(selectedSurvey.kind) && (
                        <button className={activeTab === "private" ? "active" : ""} onClick={() => loadPrivateResponses(selectedSurvey.slug)}>
                            {isDigitalAttendanceKind(selectedSurvey.kind) ? "Attendance Register" : "RSVP Review"}
                        </button>
                    )}
	                </div>
	            )}

            {activeTab === "hub" && (
                <section className="survey-results-card wide survey-builder-panel">
                    <div className="survey-builder-heading">
                        <div>
                            <p className="survey-eyebrow">Member Survey Builder</p>
                            <h3>Survey Hub</h3>
                            <p className="survey-muted">
                                Keep the closed research survey visible as a reference, and create new token-protected quick surveys from the same workspace.
                            </p>
                        </div>
                    </div>

                    <div className="survey-hub-grid">
                        <div className="survey-hub-card legacy">
                            <span className="survey-card-kicker">Closed Survey</span>
                            <h4>Behavioural Study of Freemasonry</h4>
                            <p>
                                {legacySurvey?.description || "Public summary of the anonymous membership, engagement, and retention survey."}
                            </p>
                            <div className="survey-hub-stats">
                                <strong>{legacySurvey?.responseCount ?? 49}</strong>
                                <span>anonymous responses</span>
                            </div>
                            <button type="button" onClick={loadLegacyResults}>
                                Open Analysis
                            </button>
                        </div>

                        {user && (
                            <div className={`survey-hub-card builder ${canBuild ? "" : "locked"}`}>
                                <span className="survey-card-kicker">New Survey</span>
                                <h4>Quick RSVP and Attendance Builder</h4>
                                <p>Create either an RSVP forecast before a meeting, or a simple digital attendance register that sits beside the physical register and feeds the meeting record.</p>

                                {!canBuild && (
                                    <div className="survey-login-card">
                                        <strong>Member access required</strong>
                                        <span>Log in as a member to create an RSVP forecast. Moderation and archive tools stay limited to secretaries, admins, and archivists.</span>
                                    </div>
                                )}

                                <form
                                    className={`survey-builder-form ${canBuild ? "" : "locked-preview"}`}
                                    onSubmit={canBuild ? createQuickAttendanceSurvey : (event) => event.preventDefault()}
                                    aria-hidden={!canBuild}
                                >
                                <label className="wide">
                                    Form type
                                    <div className="survey-builder-kind" role="group" aria-label="Form type">
                                        <button
                                            type="button"
                                            className={builderForm.kind === "meeting_attendance" ? "active" : ""}
                                            onClick={() => updateBuilderForm("kind", "meeting_attendance")}
                                            disabled={!canBuild}
                                        >
                                            RSVP forecast
                                            <span>Before the meeting: Yes / No planning numbers.</span>
                                        </button>
                                        <button
                                            type="button"
                                            className={builderForm.kind === "digital_attendance" ? "active" : ""}
                                            onClick={() => updateBuilderForm("kind", "digital_attendance")}
                                            disabled={!canBuild}
                                        >
                                            Digital attendance register
                                            <span>At the meeting: member / visitor attendance capture.</span>
                                        </button>
                                    </div>
                                </label>
                                <label>
                                    {isBuildingDigitalAttendance ? "Register title" : "Survey title"}
                                    <input
                                        type="text"
                                        value={generatedBuilderTitle}
                                        readOnly
                                        disabled={!canBuild}
                                        required
                                    />
                                </label>
                                <label>
                                    Lodge
                                    <Select
                                        classNamePrefix="survey-select"
                                        value={[
                                            ...lodges.map((lodge) => ({ value: lodge.label, label: lodge.label })),
                                            { value: "__other__", label: "Other lodge/body" },
                                        ].find((option) => option.value === builderForm.lodge) || null}
                                        onChange={(option) => updateBuilderForm("lodge", option?.value || "")}
                                        options={[
                                            ...lodges.map((lodge) => ({ value: lodge.label, label: lodge.label })),
                                            { value: "__other__", label: "Other lodge/body" },
                                        ]}
                                        placeholder="Select lodge"
                                        isClearable
                                        menuPortalTarget={getSelectPortalTarget()}
                                        menuPosition="fixed"
                                        isDisabled={!canBuild}
                                    />
                                </label>
                                {builderForm.lodge === "__other__" && (
                                    <label>
                                        Other lodge/body
                                        <input
                                            type="text"
                                            value={builderForm.otherLodge || ""}
                                            onChange={(event) => updateBuilderForm("otherLodge", event.target.value)}
                                            placeholder="Enter lodge or body name"
                                            disabled={!canBuild}
                                        />
                                    </label>
                                )}
                                <label>
                                    Meeting date
                                    <input
                                        type="date"
                                        value={builderForm.meetingDate}
                                        onChange={(event) => updateBuilderForm("meetingDate", event.target.value)}
                                        disabled={!canBuild}
                                    />
                                </label>
                                <label>
                                    Expiry date
                                    <input
                                        type="date"
                                        value={builderForm.expiresAt}
                                        onChange={(event) => updateBuilderForm("expiresAt", event.target.value)}
                                        disabled={!canBuild}
                                    />
                                </label>
                                <label>
                                    Meeting type
                                    <Select
                                        classNamePrefix="survey-select"
                                        value={MEETING_TYPE_OPTIONS.find((option) => option.value === builderForm.meetingType) || null}
                                        onChange={(option) => updateBuilderForm("meetingType", option?.value || "")}
                                        options={MEETING_TYPE_OPTIONS}
                                        placeholder="Select meeting type"
                                        menuPortalTarget={getSelectPortalTarget()}
                                        menuPosition="fixed"
                                        isDisabled={!canBuild}
                                    />
                                </label>
                                <label className={`survey-builder-token-field token-${builderTokenStatus.state}`}>
                                    Survey token
                                    <input
                                        type="text"
                                        value={builderForm.token}
                                        onChange={(event) => updateBuilderForm("token", event.target.value)}
                                        placeholder="Create a simple shared token"
                                        minLength={4}
                                        disabled={!canBuild}
                                        aria-invalid={builderTokenStatus.state !== "available"}
                                        aria-describedby="survey-builder-token-status"
                                        required
                                    />
                                    <span id="survey-builder-token-status" className="survey-token-status">
                                        {builderTokenStatus.message}
                                    </span>
                                </label>
                                <div className="survey-builder-recipient-panels wide">
                                    <details
                                        className="survey-builder-recipient-panel"
                                        open={builderRecipientPanels.invite}
                                        onToggle={(event) => {
                                            const open = event.currentTarget.open;
                                            setBuilderRecipientPanels((current) => ({ ...current, invite: open }));
                                        }}
                                    >
                                        <summary>
                                            <span>
                                                <strong>{isBuildingDigitalAttendance ? "Attendance link recipients" : "RSVP invite recipients"}</strong>
                                                <small>{getRecipientPanelSummary(builderForm.inviteMemberEmails, builderForm.inviteEmails)}</small>
                                            </span>
                                            <button type="button" onClick={(event) => {
                                                event.preventDefault();
                                                toggleBuilderRecipientPanel("invite");
                                            }}>
                                                {builderRecipientPanels.invite ? "Hide" : "Edit"}
                                            </button>
                                        </summary>
                                        <div className="survey-builder-recipient-body">
                                            {canUseRecipientDirectory && (
                                                <label>
                                                    Choose from members and saved recipients
                                                    <Select
                                                        classNamePrefix="survey-select"
                                                        value={recipientMemberOptions.filter((option) =>
                                                            (builderForm.inviteMemberEmails || []).includes(option.value)
                                                        )}
                                                        onChange={(options) => updateBuilderForm(
                                                            "inviteMemberEmails",
                                                            (options || []).map((option) => option.value)
                                                        )}
                                                        options={groupedRecipientOptions}
                                                        formatOptionLabel={formatRecipientOptionLabel}
                                                        placeholder={isBuildingDigitalAttendance
                                                            ? "Choose who receives the attendance register link"
                                                            : "Choose who receives the RSVP link and token"}
                                                        isMulti
                                                        isSearchable
                                                        menuPortalTarget={getSelectPortalTarget()}
                                                        menuPosition="fixed"
                                                        isDisabled={!canBuild}
                                                    />
                                                </label>
                                            )}
                                            <label>
                                                Additional emails
                                                <textarea
                                                    rows={2}
                                                    value={builderForm.inviteEmails}
                                                    onChange={(event) => updateBuilderForm("inviteEmails", event.target.value)}
                                                    placeholder={isBuildingDigitalAttendance
                                                        ? "Extra emails to receive the attendance register link now"
                                                        : "Extra emails to receive the RSVP link and token now"}
                                                    disabled={!canBuild}
                                                />
                                            </label>
                                            <span className="survey-field-note">
                                                {isBuildingDigitalAttendance
                                                    ? "These recipients receive the attendance register link, meeting details, token, and QR code for copying into an agenda or notice."
                                                    : "These recipients receive the RSVP link, open/close dates, meeting details, token, and QR code."}
                                            </span>
                                        </div>
                                    </details>
                                    <details
                                        className="survey-builder-recipient-panel"
                                        open={builderRecipientPanels.summary}
                                        onToggle={(event) => {
                                            const open = event.currentTarget.open;
                                            setBuilderRecipientPanels((current) => ({ ...current, summary: open }));
                                        }}
                                    >
                                        <summary>
                                            <span>
                                                <strong>Results summary recipients</strong>
                                                <small>{getRecipientPanelSummary(builderForm.notificationMemberEmails, builderForm.notificationEmails)}</small>
                                            </span>
                                            <button type="button" onClick={(event) => {
                                                event.preventDefault();
                                                toggleBuilderRecipientPanel("summary");
                                            }}>
                                                {builderRecipientPanels.summary ? "Hide" : "Edit"}
                                            </button>
                                        </summary>
                                        <div className="survey-builder-recipient-body">
                                            {canUseRecipientDirectory && (
                                                <label>
                                                    Choose from members and saved recipients
                                                    <Select
                                                        classNamePrefix="survey-select"
                                                        value={recipientMemberOptions.filter((option) =>
                                                            (builderForm.notificationMemberEmails || []).includes(option.value)
                                                        )}
                                                        onChange={(options) => updateBuilderForm(
                                                            "notificationMemberEmails",
                                                            (options || []).map((option) => option.value)
                                                        )}
                                                        options={groupedRecipientOptions}
                                                        formatOptionLabel={formatRecipientOptionLabel}
                                                        placeholder="Choose who receives the close summary"
                                                        isMulti
                                                        isSearchable
                                                        menuPortalTarget={getSelectPortalTarget()}
                                                        menuPosition="fixed"
                                                        isDisabled={!canBuild}
                                                    />
                                                </label>
                                            )}
                                            <label>
                                                Additional emails
                                                <textarea
                                                    rows={2}
                                                    value={builderForm.notificationEmails}
                                                    onChange={(event) => updateBuilderForm("notificationEmails", event.target.value)}
                                                    placeholder={canUseRecipientDirectory ? "Extra summary emails, separated by commas" : "Secretary, WM, caterer, or other close-summary emails"}
                                                    disabled={!canBuild}
                                                />
                                            </label>
                                            <span className="survey-field-note">
                                                These people receive the result summary when the form closes. The app will also try to email the lodge Master and Secretary from officer records.
                                            </span>
                                        </div>
                                    </details>
                                </div>
                                {!isBuildingDigitalAttendance && (
                                    <div className="survey-builder-invite-image wide">
                                        <div>
                                            <strong>RSVP invite image</strong>
                                            <span>
                                                {builderForm.inviteImage?.filename
                                                    ? `${builderForm.inviteImage.filename} will be included in the invite email.`
                                                    : "Optional JPG, PNG, or WebP notice image, up to 10 MB."}
                                            </span>
                                        </div>
                                        {builderForm.inviteImage?.path && (
                                            <img
                                                src={builderForm.inviteImage.path}
                                                alt="RSVP invite attachment preview"
                                            />
                                        )}
                                        <div className="survey-builder-invite-image-actions">
                                            <label className="secondary file-button">
                                                {inviteImageUploading ? "Uploading..." : builderForm.inviteImage ? "Replace Image" : "Add Image"}
                                                <input
                                                    type="file"
                                                    accept="image/png,image/jpeg,image/webp"
                                                    onChange={uploadInviteImage}
                                                    disabled={!canBuild || inviteImageUploading}
                                                />
                                            </label>
                                            {builderForm.inviteImage && (
                                                <button
                                                    type="button"
                                                    className="secondary"
                                                    onClick={() => updateBuilderForm("inviteImage", null)}
                                                    disabled={!canBuild || inviteImageUploading}
                                                >
                                                    Remove
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                )}
                                <label className="wide">
                                    Optional note
                                    <textarea
                                        rows={3}
                                        value={builderForm.description}
                                        onChange={(event) => updateBuilderForm("description", event.target.value)}
                                        placeholder="Optional wording shown above the survey"
                                        disabled={!canBuild}
                                    />
                                </label>
                                <div className="survey-question-builder fixed wide">
                                    <div className="survey-question-builder-head">
                                        <div>
                                            <strong>Respondents will answer</strong>
                                            <span>
                                                {isBuildingDigitalAttendance
                                                    ? "The register asks only for name, email, and whether the person is a member of this lodge or a visitor. It creates the meeting record if needed."
                                                    : "This experimental RSVP tool uses a fixed short form. Public results show anonymous likely-attendance and lodge totals, never names or email addresses."}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="survey-default-questions">
                                        {(isBuildingDigitalAttendance
                                            ? ["Name and surname", "Email address", "Member of this lodge or visitor", "Visitor lodge/body"]
                                            : ["Name and surname", "Email address", "Which lodge are you attending from?", "Will you attend?", "Optional apologies / note if not attending"]
                                        ).map((label) => (
                                            <span key={label}>{label}</span>
                                        ))}
                                    </div>
                                </div>
                                    <button type="submit" className="survey-submit" disabled={!canBuild}>
                                        {isBuildingDigitalAttendance ? "Create Attendance Register" : "Create RSVP Survey"}
                                    </button>
                                </form>

                                {createdSurvey && (
                                    <div className="survey-created-card">
                                        <strong>{createdSurvey.title}</strong>
                                        <span>URL: {`${window.location.origin}${createdSurvey.responseUrl}`}</span>
                                        <span>Token: {createdSurvey.token}</span>
                                        {isRsvpSurveyKind(createdSurvey.kind) && <>
                                            <a href={`/rsvp?survey=${encodeURIComponent(createdSurvey.slug)}#token=${encodeURIComponent(createdSurvey.token)}`}>Open simple RSVP: name, lodge and Yes/No</a>
                                            <a target="_blank" rel="noopener noreferrer" href={`https://wa.me/?text=${encodeURIComponent(`${createdSurvey.title}\nPlease let us know whether you will attend:\n${window.location.origin}/rsvp?survey=${encodeURIComponent(createdSurvey.slug)}#token=${encodeURIComponent(createdSurvey.token)}`)}`}>Share simple RSVP on WhatsApp</a>
                                            <small>Anyone with this shared link can respond. Use the same link in every group.</small>
                                        </>}
                                        {createdSurvey.expires_at && (
                                            <span>Closes: {new Date(createdSurvey.expires_at).toLocaleDateString("en-ZA")}</span>
                                        )}
                                        {canBuild && isMeetingFormSurveyKind(createdSurvey.kind) && (
                                            <button
                                                type="button"
                                                className="secondary"
                                                onClick={() => sendRsvpInviteEmail(createdSurvey.slug, createdSurvey.token)}
                                            >
                                                Send {isDigitalAttendanceKind(createdSurvey.kind) ? "Attendance" : "RSVP"} Invite
                                            </button>
                                        )}
                                        <button type="button" className="secondary" onClick={verifyToken}>
                                            Open New Form
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="survey-hub-card current" id="meeting-registers">
                            <span className="survey-card-kicker">Current Surveys</span>
                            <h4>{permanentLodgeId ? `Open meeting forms · ${surveyFilters.lodge && surveyFilters.lodge !== permanentLodgeId ? surveyFilters.lodge : surveyFilters.lodge ? "this lodge" : "All lodges"}` : "Meeting RSVPs and Attendance Registers"}</h4>
                            {permanentLodgeId && <p>Your lodge is selected below. Its open RSVPs and attendance registers are shown. Choose Show all lodges to browse other lodges’ open meeting forms.</p>}
                            <p>
                                RSVP forecasts help with planning before a meeting. Digital registers help capture a clean attendance copy on the day while the physical register remains the official record.
                            </p>
                            <p className="survey-permanent-qr-link">
                                <Link to="/attendance">View permanent lodge QR codes →</Link>
                            </p>
                            <div className="survey-list-filters" aria-label="Survey list filters">
                                <label>
                                    Lodge
                                    <Select
                                        classNamePrefix="survey-select"
                                        value={surveyLodgeFilterOptions.find((option) => option.value === surveyFilters.lodge) || null}
                                        onChange={(option) => setSurveyFilters((current) => ({
                                            ...current,
                                            lodge: option?.value || "",
                                        }))}
                                        options={surveyLodgeFilterOptions}
                                        placeholder={surveyLodgeFilterOptions.length ? "Search by lodge" : "No lodge filters yet"}
                                        isClearable
                                        isSearchable
                                        menuPortalTarget={getSelectPortalTarget()}
                                        menuPosition="fixed"
                                    />
                                </label>
                                <label>
                                    Form type
                                    <Select
                                        classNamePrefix="survey-select"
                                        value={surveyKindFilterOptions.find((option) => option.value === surveyFilters.kind)}
                                        onChange={(option) => setSurveyFilters((current) => ({
                                            ...current,
                                            kind: option?.value || "all",
                                        }))}
                                        options={surveyKindFilterOptions}
                                        placeholder="Choose form type"
                                        isSearchable={false}
                                        menuPortalTarget={getSelectPortalTarget()}
                                        menuPosition="fixed"
                                    />
                                </label>
                                {canReviewRsvpResponses && (
                                    <label className="survey-archive-toggle">
                                        <input
                                            type="checkbox"
                                            checked={surveyFilters.includeArchived}
                                            onChange={(event) => setSurveyFilters((current) => ({
                                                ...current,
                                                includeArchived: event.target.checked,
                                            }))}
                                        />
                                        Include archived
                                    </label>
                                )}
                            </div>
                            <div className="survey-current-list">
                                {filteredSurveyList.map((item) => {
                                    const expired = isSurveyExpired(item);
                                    const isDigitalRegister = isDigitalAttendanceKind(item.kind);
                                    return (
                                    <article
                                        key={item.slug}
                                        className={`survey-current-item meeting-${getMeetingTypeClass(item.meeting?.type)} ${expired ? "expired" : ""} ${selectedSurvey.slug === item.slug ? "active" : ""}`}
                                    >
                                        <button
                                            type="button"
                                            className="survey-current-open"
                                            onClick={() => {
                                                if (expired) {
                                                    loadClosedSurveyResults(item.slug);
                                                    return;
                                                }
                                                setSelectedSlug(item.slug);
                                                if (canReviewRsvpResponses && isMeetingFormSurveyKind(item.kind)) {
                                                    setActiveTab("private");
                                                    loadPrivateResponses(item.slug);
                                                } else {
                                                    setActiveTab("survey");
                                                }
                                            }}
                                        >
                                            <span className="survey-current-topline">
                                                <em className="survey-type-badge">{isDigitalRegister ? "Attendance register" : item.meeting?.type || "Survey"}</em>
                                                {(expired || item.archiveState === "archived") && <em className="survey-status-badge">{item.archiveState === "archived" ? "Archived" : "Closed"}</em>}
                                                {item.expires_at && <small>{expired ? "Closed" : "Closes"} {formatSurveyDate(item.expires_at.slice(0, 10))}</small>}
                                            </span>
                                            <strong>{item.meeting?.lodge || item.title}</strong>
                                            <span className="survey-current-meta">
                                                <b>{item.meeting?.date ? formatSurveyDate(item.meeting.date) : "No date"}</b>
                                                <i>{item.title}</i>
                                            </span>
                                            {isDigitalRegister ? (
                                                <span className="survey-rsvp-badges" aria-label="Attendance register summary">
                                                    <span className="survey-rsvp-badge member">
                                                        <strong>{Number(item.member_count || 0).toLocaleString()}</strong>
                                                        <small>Member{Number(item.member_count || 0) === 1 ? "" : "s"}</small>
                                                    </span>
                                                    <span className="survey-rsvp-badge visitor">
                                                        <strong>{Number(item.visitor_count || 0).toLocaleString()}</strong>
                                                        <small>Visitor{Number(item.visitor_count || 0) === 1 ? "" : "s"}</small>
                                                    </span>
                                                    <span className="survey-rsvp-badge total">
                                                        <strong>{Number(item.response_count || 0).toLocaleString()}</strong>
                                                        <small>Total</small>
                                                    </span>
                                                </span>
                                            ) : (
                                                <span className="survey-rsvp-badges" aria-label="RSVP response summary">
                                                    <span className="survey-rsvp-badge yes">
                                                        <strong>{Number(item.attending_count || 0).toLocaleString()}</strong>
                                                        <small>Yes</small>
                                                    </span>
                                                    <span className="survey-rsvp-badge no">
                                                        <strong>{Number(item.not_attending_count || 0).toLocaleString()}</strong>
                                                        <small>No</small>
                                                    </span>
                                                    <span className="survey-rsvp-badge total">
                                                        <strong>{Number(item.response_count || 0).toLocaleString()}</strong>
                                                        <small>Total</small>
                                                    </span>
                                                </span>
                                            )}
                                        </button>
                                        {(canReviewRsvpResponses || canManageSurveys) && (
                                            <div className="survey-current-actions">
                                                {canReviewRsvpResponses && isMeetingFormSurveyKind(item.kind) && (
                                                    <button
                                                        type="button"
                                                        className="secondary"
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            setSelectedSlug(item.slug);
                                                            setActiveTab("private");
                                                            loadPrivateResponses(item.slug);
                                                        }}
                                                    >
                                                        Review / Email
                                                    </button>
                                                )}
                                                {canManageSurveys && (
                                                    <>
                                                        <button type="button" className="secondary" onClick={() => {
                                                            const date = new Date(`${item.meeting?.date || new Date().toISOString().slice(0, 10)}T12:00:00Z`);
                                                            date.setUTCDate(date.getUTCDate() + 1);
                                                            setClosingDateEditor({ slug: item.slug, date: date.toISOString().slice(0, 10) });
                                                        }}>
                                                            {expired ? "Reopen / Change closing date" : "Change closing date"}
                                                        </button>
                                                        <button type="button" className="secondary" onClick={(event) => archiveSurvey(event, item.slug)}>
                                                            Archive
                                                        </button>
                                                        <button type="button" className="survey-danger" onClick={(event) => deleteSurvey(event, item.slug, item.title)}>
                                                            Delete
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        )}
                                        {closingDateEditor?.slug === item.slug && (
                                            <form onSubmit={saveClosingDate} className="survey-closing-date-editor">
                                                <label>Closing date
                                                    <input type="date" required value={closingDateEditor.date} onChange={event => setClosingDateEditor(current => ({ ...current, date: event.target.value }))} />
                                                </label>
                                                <p>Closes at 23:59 South African time. Existing responses are kept.</p>
                                                <button type="submit" disabled={savingClosingDate}>{savingClosingDate ? "Saving…" : "Save and open"}</button>
                                                <button type="button" className="secondary" disabled={savingClosingDate} onClick={() => setClosingDateEditor(null)}>Cancel</button>
                                            </form>
                                        )}
                                    </article>
                                    );
                                })}
                                {filteredSurveyList.length === 0 && (
                                    <p className="survey-muted">
                                        {searchableSurveys.length
                                            ? "No surveys match those filters."
                                            : "No quick surveys have been created yet."}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {isVerifiedSurveyOpen && activeTab === "survey" && (
                <form className="survey-form" onSubmit={submitSurvey}>
                    <div className={`survey-completion ${isSurveyComplete ? "complete" : "incomplete"}`}>
                        <div>
                            <strong>{isSurveyComplete ? "Go: ready to submit" : "Stop: questions still missing"}</strong>
                            <span>{answeredQuestionCount} of {visibleQuestions.length} questions complete</span>
                        </div>
                        <em>{isSurveyComplete ? "All answered" : `${missingQuestions.length} remaining`}</em>
                    </div>
                    {responderMemoryNotice && (
                        <div className="survey-alert flow-note">
                            {responderMemoryNotice}
                        </div>
                    )}
                    {sections.map((section, sectionIndex) => (
                        <section className={`survey-section theme-${getSectionTheme(section)}`} key={section.title}>
                            <div className="survey-section-title">
                                <span>Section {sectionIndex + 1} of {sections.length}</span>
                                <h2>{section.title}</h2>
                                <em>{getSectionThemeLabel(section)}</em>
                            </div>
                            {(section.questions || []).filter((question) => isQuestionVisible(question, answers)).map((question) => {
                                const answered = isQuestionAnswered(question, answers);
                                const required = isQuestionRequired(question);
                                return (
                                    <fieldset className={`survey-question ${answered ? "answered" : required ? "missing" : "optional"}`} key={question.id}>
                                        <legend>
                                            {getDisplayQuestionLabel(question)}
                                            <span className={`survey-required-status ${required ? "" : "optional"}`}>
                                                {answered ? "Answered" : required ? "Required" : "Optional"}
                                            </span>
                                        </legend>
                                        <p className="survey-question-instruction">{getQuestionInstruction(question)}</p>
                                        {renderQuestion(question)}
                                    </fieldset>
                                );
                            })}
                        </section>
                    ))}
                    <button type="submit" className="survey-submit" disabled={!isSurveyComplete || submitting}>
                        {submitting
                            ? "Submitting..."
                            : isRsvpSurveyKind(selectedSurvey.kind)
                                ? "Submit RSVP"
                                : isDigitalAttendanceKind(selectedSurvey.kind)
                                    ? "Record Attendance"
                                    : "Submit Anonymous Response"}
                    </button>
                </form>
            )}

            {activeTab === "results" && results?.slug === DEFAULT_SLUG && (
                <ClosedSurveyAnalysis results={results} />
            )}

            {activeTab === "results" && results && results?.slug !== DEFAULT_SLUG && isMeetingAttendanceResults(results, selectedSurvey) && (
                <MeetingAttendanceResults
                    results={results}
                    canManageSurveys={canReviewRsvpResponses}
                    privateResponses={privateResponses}
                    onLoadPrivateResponses={() => loadPrivateResponses(results.slug)}
                />
            )}

            {activeTab === "results" && results && results?.slug !== DEFAULT_SLUG && !isMeetingAttendanceResults(results, selectedSurvey) && (
                <section className="survey-results">
                    <div className="survey-summary-grid">
                        <div>
                            <strong>{results.responseCount}</strong>
                            <span>responses</span>
                        </div>
                        <div>
                            <strong>{recommendCount}</strong>
                            <span>would recommend</span>
                        </div>
                        <div>
                            <strong>{Object.keys(results.submittedByDay || {}).length}</strong>
                            <span>response days</span>
                        </div>
                    </div>

                    {results.responseCount < 10 && (
                        <div className="survey-alert low-sample">
                            Results are still based on a small sample. Counts are useful now; the chart shapes will become more meaningful as more brethren respond.
                        </div>
                    )}

                    {results.relationshipFlows?.length > 0 && (
                        <>
                            <h2 className="survey-results-heading">Linked Response Patterns</h2>
                            <div className="survey-alert flow-note">
                                Sankey ribbons aggregate anonymous responses. Thicker ribbons mean more people shared the same route across answers. With fewer than 10 responses, these are early paths rather than firm patterns.
                            </div>
                            <div className="survey-flow-grid">
                                {results.relationshipFlows.map((flow) => (
                                    <SurveyFlowMap key={flow.id} flow={flow} responseCount={results.responseCount} />
                                ))}
                            </div>
                        </>
                    )}

                    {backgroundCharts.length > 0 && (
                        <>
                            <h2 className="survey-results-heading theme-heading-background">Background</h2>
                            <div className="survey-results-grid">
                                {backgroundCharts.filter((question) => !["age_band", "age_at_joining"].includes(question.id)).map((question) => (
                                    <BarList key={question.id} title={question.label} counts={question.counts} total={question.total} />
                                ))}
                            </div>
                        </>
                    )}

                    {beforeCharts.length > 0 && (
                        <>
                            <h2 className="survey-results-heading theme-heading-before">Before Joining</h2>
                            <div className="survey-results-grid">
                                {beforeCharts.map((question) => (
                                    <BarList key={question.id} title={question.label} counts={question.counts} total={question.total} />
                                ))}
                            </div>
                        </>
                    )}

                    <SurveyInsightDashboard results={results} />

                    <div className="survey-insight-grid">
                        <div className="survey-results-card">
                            <h3>Participation Pulse</h3>
                            <p>
                                Current activity, attendance frequency, connection, recommendation, and join-again answers are grouped here for the quickest read on health.
                            </p>
                        </div>
                        {results.highlights?.topBarriers?.length > 0 && (
                            <DonutBreakdown title="Top Barriers" rows={results.highlights.topBarriers} />
                        )}
                    </div>

                    {coreCharts.length > 0 && (
                        <>
                            <h2 className="survey-results-heading theme-heading-current">Current Experience and Engagement</h2>
                            <EngagementHealthChart questions={coreCharts} />
                        </>
                    )}

                    {matrixQuestions.map((question) => (
                        <LikertStack key={question.id} question={question} />
                    ))}

                    {currentDetailCharts.length > 0 && (
                        <>
                            <div className="survey-results-grid">
                                {currentDetailCharts.map((question) => (
                                    <BarList key={question.id} title={question.label} counts={question.counts} total={question.total} />
                                ))}
                            </div>
                        </>
                    )}

                    {otherCharts.length > 0 && (
                        <div className="survey-results-grid">
                            {otherCharts.map((question) => (
                                <BarList key={question.id} title={question.label} counts={question.counts} total={question.total} />
                            ))}
                        </div>
                    )}

                    {textQuestions.length > 0 && (
                        <div className="survey-results-card wide">
                            <h3>Written Reflections</h3>
                            {textQuestions.map((question) => (
                                <details key={question.id}>
                                    <summary>{question.label} ({question.textResponses.length})</summary>
                                    <ul>
                                        {question.textResponses.slice(0, 30).map((text, index) => (
                                            <li key={`${question.id}-${index}`}>{text}</li>
                                        ))}
                                    </ul>
                                </details>
                            ))}
                        </div>
                    )}

                    {(barrierCharts.length > 0 || practicalBarrierCharts.length > 0) && (
                        <>
                            <h2 className="survey-results-heading theme-heading-barriers">Perceived Barriers and Practical Realities</h2>
                            <div className="survey-results-grid">
                                {[...barrierCharts, ...practicalBarrierCharts].map((question) => (
                                    <BarList key={question.id} title={question.label} counts={question.counts} total={question.total} />
                                ))}
                            </div>
                        </>
                    )}
                </section>
            )}

            {activeTab === "moderate" && canModerate && (
                <section className="survey-results-card wide">
                    <h3>Response Moderation</h3>
                    <p className="survey-muted">
                        Responses are listed by latest submission time. RSVP forecasts show one current response per person, with changed responses marked for review.
                    </p>

                    {editingResponseId && (
                        <div className="survey-response-editor">
                            <div className="survey-response-editor-header">
                                <div>
                                    <span>Editing anonymous response</span>
                                    <strong>{editingResponseId}</strong>
                                </div>
                                <div>
                                    <button type="button" className="secondary" onClick={() => {
                                        setEditingResponseId(null);
                                        setEditAnswers({});
                                    }}>
                                        Cancel
                                    </button>
                                    <button type="button" onClick={saveResponseEdit}>
                                        Save Changes
                                    </button>
                                </div>
                            </div>
                            {sections.map((section) => (
                                <div className={`survey-response-editor-section theme-${getSectionTheme(section)}`} key={`edit-${section.title}`}>
                                    <h4>{section.title}</h4>
                                    {(section.questions || [])
                                        .filter((question) => isQuestionVisible(question, editAnswers))
                                        .map((question) => (
                                            <fieldset className="survey-question compact-edit" key={`edit-${question.id}`}>
                                                <legend>{question.label}</legend>
                                                <p className="survey-question-instruction">{getQuestionInstruction(question)}</p>
                                                {renderQuestion(question, editAnswers, setEditAnswers, `edit-${editingResponseId}`)}
                                            </fieldset>
                                        ))}
                                </div>
                            ))}
                        </div>
                    )}

                    <table className="survey-table survey-response-table">
                        <thead>
                        <tr>
                            <th>Submitted</th>
                            <th>Changed</th>
                            {isMeetingFormSurveyKind(selectedSurvey.kind) && (
	                                <>
	                                    <th>Name</th>
	                                    <th>Email</th>
	                                    <th>Lodge</th>
	                                    <th>{isDigitalAttendanceKind(selectedSurvey.kind) ? "Role" : "Attend"}</th>
	                                </>
                            )}
                            <th>Response</th>
                            <th>Quick read</th>
                            <th>Action</th>
                        </tr>
                        </thead>
                        <tbody>
                        {responses.map((response) => (
                            <tr key={response.id}>
                                <td>{new Date(response.submitted_at).toLocaleString("en-ZA")}</td>
                                <td>{Number(response.revision_count || 0) > 0 ? `${response.revision_count} update${Number(response.revision_count || 0) === 1 ? "" : "s"}` : "No"}</td>
	                                {isMeetingFormSurveyKind(selectedSurvey.kind) && (
	                                    <>
	                                        <td>{getRsvpAnswer(response, "name") || "Not given"}</td>
	                                        <td>{getRsvpAnswer(response, "email_address") || "Not given"}</td>
	                                        <td>{getRsvpAnswer(response, "lodge_affiliation") || "Not given"}</td>
	                                        <td>{getRsvpAnswer(response, isDigitalAttendanceKind(selectedSurvey.kind) ? "attendance_role" : "attendance_status") || "Not given"}</td>
	                                    </>
                                )}
                                <td>{response.id}</td>
                                <td>
                                    {isMeetingFormSurveyKind(selectedSurvey.kind)
	                                        ? [
	                                            getRsvpAnswer(response, "name"),
	                                            getRsvpAnswer(response, "email_address"),
	                                            getRsvpAnswer(response, "lodge_affiliation"),
	                                            getRsvpAnswer(response, isDigitalAttendanceKind(selectedSurvey.kind) ? "attendance_role" : "attendance_status"),
	                                        ].filter(Boolean).join(" · ") || "No RSVP fields"
                                        : [
                                            response.answers?.current_activity,
                                            response.answers?.recommend_to_friend,
                                            response.answers?.age_band,
                                        ].filter(Boolean).join(" · ") || "No quick fields"}
                                </td>
                                <td>
                                    <button type="button" className="secondary" onClick={() => beginResponseEdit(response)}>
                                        Edit
                                    </button>
                                    <button type="button" className="survey-danger" onClick={() => deleteResponse(response.id)}>
                                        Delete
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {responses.length === 0 && (
                            <tr>
	                                <td colSpan={isMeetingFormSurveyKind(selectedSurvey.kind) ? "9" : "5"}>No responses loaded.</td>
                            </tr>
                        )}
                        </tbody>
                    </table>
                </section>
            )}

            {activeTab === "private" && canReviewRsvpResponses && (
                <section className="survey-results-card wide">
                    <div className="survey-private-head">
                        <div>
                            <h3>{isDigitalAttendanceKind(selectedSurvey.kind) ? "Attendance Register Review" : "RSVP Review"}</h3>
                            <p className="survey-muted">
                                This view is available to logged-in members with review access. Public/token result views keep private names and email addresses out of aggregate results.
                            </p>
                        </div>
                        {rsvpSurveyOptions.length > 0 && (
                            <div className="survey-private-tools">
                                <label className="survey-private-picker">
                                    {isDigitalAttendanceKind(selectedSurvey.kind) ? "Review register" : "Review forecast"}
                                    <Select
                                        classNamePrefix="survey-select"
                                        value={rsvpSurveyOptions.find((option) => option.value === selectedSurvey.slug) || null}
                                        onChange={(option) => loadPrivateResponses(option?.value)}
                                        options={rsvpSurveyOptions}
                                        placeholder={isDigitalAttendanceKind(selectedSurvey.kind) ? "Choose attendance register" : "Choose RSVP forecast"}
                                        isSearchable
                                        menuPortalTarget={getSelectPortalTarget()}
                                        menuPosition="fixed"
                                    />
                                </label>
                                {canManageSurveys && (
                                    <div className="survey-recipient-editor">
                                        <div className="survey-recipient-group">
                                            <strong>{isDigitalAttendanceKind(selectedSurvey.kind) ? "Attendance invite recipients" : "RSVP invite recipients"}</strong>
                                            <label>
                                                Invite members
                                                <Select
                                                    classNamePrefix="survey-select"
                                                    value={recipientMemberOptions.filter((option) =>
                                                        inviteRecipientMemberEmails.includes(option.value)
                                                    )}
                                                    onChange={(options) => setInviteRecipientMemberEmails(
                                                        (options || []).map((option) => option.value)
                                                    )}
                                                    options={groupedRecipientOptions}
                                                    formatOptionLabel={formatRecipientOptionLabel}
                                                    placeholder={isDigitalAttendanceKind(selectedSurvey.kind)
                                                        ? "Choose attendance invite recipients"
                                                        : "Choose RSVP invite recipients"}
                                                    isMulti
                                                    isSearchable
                                                    menuPortalTarget={getSelectPortalTarget()}
                                                    menuPosition="fixed"
                                                />
                                            </label>
                                            <label>
                                                Manual invite emails
                                                <textarea
                                                    rows={2}
                                                    value={inviteRecipientExtraEmails}
                                                    onChange={(event) => setInviteRecipientExtraEmails(event.target.value)}
                                                    placeholder="visitors@example.com, members@example.com"
                                                />
                                            </label>
                                            <label>
                                                Invite token
                                                <input
                                                    type="text"
                                                    value={inviteToken}
                                                    onChange={(event) => setInviteToken(event.target.value)}
                                                    placeholder={isDigitalAttendanceKind(selectedSurvey.kind)
                                                        ? "Attendance token to include in invite"
                                                        : "RSVP token to include in invite"}
                                                />
                                            </label>
                                            <div className="survey-recipient-actions">
                                                <button type="button" className="secondary" onClick={saveRsvpInviteRecipients}>
                                                    Save Invite List
                                                </button>
                                                <button type="button" className="secondary" onClick={() => sendRsvpInviteEmail()}>
                                                    Send {isDigitalAttendanceKind(selectedSurvey.kind) ? "Attendance" : "RSVP"} Invite
                                                </button>
                                            </div>
                                        </div>
                                        <div className="survey-recipient-group">
                                            <strong>Close-summary recipients</strong>
                                        <label>
                                            Summary members
                                            <Select
                                                classNamePrefix="survey-select"
                                                value={recipientMemberOptions.filter((option) =>
                                                    summaryRecipientMemberEmails.includes(option.value)
                                                )}
                                                onChange={(options) => setSummaryRecipientMemberEmails(
                                                    (options || []).map((option) => option.value)
                                                )}
                                                options={groupedRecipientOptions}
                                                formatOptionLabel={formatRecipientOptionLabel}
                                                placeholder="Choose member emails"
                                                isMulti
                                                isSearchable
                                                menuPortalTarget={getSelectPortalTarget()}
                                                menuPosition="fixed"
                                            />
                                        </label>
                                        <label>
                                            Manual summary emails
                                            <textarea
                                                rows={2}
                                                value={summaryRecipientExtraEmails}
                                                onChange={(event) => setSummaryRecipientExtraEmails(event.target.value)}
                                                placeholder="caterer@example.com, visitor@example.com"
                                            />
                                        </label>
                                        <div className="survey-recipient-actions">
                                            <button type="button" className="secondary" onClick={saveRsvpSummaryRecipients}>
                                                Save Summary List
                                            </button>
                                            {canSendTestEmail && (
                                                <button type="button" className="secondary survey-email-test" onClick={sendRsvpTestEmail}>
                                                    Send Test Email
                                                </button>
                                            )}
                                        </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {privateResponses.length > 0 && !isDigitalAttendanceKind(selectedSurvey.kind) && (
                        <div className="survey-private-summary">
                            <DonutBreakdown title="RSVP Forecast" rows={surveyRows(privateAttendanceCounts)} />
                            <BarList title="Potential attendees by lodge" counts={privateLodgeCounts} total={privateResponses.length} />
                        </div>
                    )}

                    <table className="survey-table survey-private-table">
                        <thead>
                        <tr>
	                            <th>Submitted</th>
	                            <th>Changed</th>
	                            <th>Name</th>
	                            <th>Email</th>
	                            <th>Lodge</th>
	                            <th>{isDigitalAttendanceKind(selectedSurvey.kind) ? "Role" : "Attend"}</th>
                            {isDigitalAttendanceKind(selectedSurvey.kind) && (
                                <>
                                    <th>Category</th>
                                    <th>Rank / office</th>
                                    <th>Secretary note</th>
                                </>
                            )}
                            <th>Other answers</th>
                        </tr>
                        </thead>
                        <tbody>
                        {privateResponses.map((response) => (
                            <tr key={response.id}>
	                                <td>{new Date(response.submitted_at).toLocaleString("en-ZA")}</td>
	                                <td>{Number(response.revision_count || 0) > 0 ? `${response.revision_count} update${Number(response.revision_count || 0) === 1 ? "" : "s"}` : "No"}</td>
	                                <td>{isDigitalAttendanceKind(selectedSurvey.kind) ? formatAttendanceName(response) : (response.name || "Not given")}</td>
	                                <td>{response.email || "Not given"}</td>
	                                <td>{response.lodge || "Not given"}</td>
	                                <td>{response.attendance || "Not given"}</td>
                                {isDigitalAttendanceKind(selectedSurvey.kind) && (
                                    <>
                                        <td>{response.memberCategory || ""}</td>
                                        <td>{formatRankOffice(response)}</td>
                                        <td>{response.note || ""}</td>
                                    </>
                                )}
                                <td>
                                    {(response.extraAnswers || []).length
                                        ? response.extraAnswers.map((answer) => `${answer.label}: ${answer.value}`).join(" · ")
                                        : ""}
                                </td>
                            </tr>
                        ))}
                        {privateResponses.length === 0 && (
                            <tr>
	                                <td colSpan={isDigitalAttendanceKind(selectedSurvey.kind) ? "10" : "7"}>No responses loaded for this survey.</td>
                            </tr>
                        )}
                        </tbody>
                    </table>
                </section>
            )}
	        </main>
	
        {pendingApologyPrompt && (
            <div className="survey-confirm-overlay" role="presentation">
                <div className="survey-confirm-modal survey-apology-modal" role="dialog" aria-modal="true" aria-labelledby="survey-apology-title">
                    <div className="survey-confirm-head">
                        <p className="survey-eyebrow">Optional secretary note</p>
                        <h3 id="survey-apology-title">Would you like to add an apology reason?</h3>
                    </div>
                    <p className="survey-muted">
                        This note is kept with the private RSVP review list and can help the Secretary record apologies accurately.
                    </p>
                    <textarea
                        className="survey-apology-textarea"
                        rows={5}
                        value={apologyDraft}
                        onChange={(event) => setApologyDraft(event.target.value)}
                        placeholder="Optional reason or message for the Secretary"
                        autoFocus
                    />
                    <div className="survey-confirm-actions">
                        <button type="button" className="secondary" onClick={() => submitApologyPrompt(false)}>
                            Submit Without Note
                        </button>
                        <button type="button" onClick={() => submitApologyPrompt(true)}>
                            Save Note and Submit
                        </button>
                    </div>
                </div>
            </div>
        )}

        {pendingCreate && (
            <div className="survey-confirm-overlay" role="presentation">
                <div className="survey-confirm-modal" role="dialog" aria-modal="true" aria-labelledby="survey-create-confirm-title">
                    <div className="survey-confirm-head">
                        <p className="survey-eyebrow">
                            {isDigitalAttendanceKind(pendingCreate.payload.kind) ? "Review attendance register" : "Review RSVP forecast"}
                        </p>
                        <h3 id="survey-create-confirm-title">
                            {isDigitalAttendanceKind(pendingCreate.payload.kind) ? "Create this attendance register?" : "Create this survey?"}
                        </h3>
                    </div>
                    <div className="survey-confirm-details">
                        <div>
                            <span>Lodge</span>
                            <strong>{pendingCreate.payload.lodge || "Not set"}</strong>
                        </div>
                        <div>
                            <span>Meeting date</span>
                            <strong>{pendingCreate.payload.meetingDate || "Not set"}</strong>
                        </div>
                        <div>
                            <span>Meeting type</span>
                            <strong>{pendingCreate.payload.meetingType || "Not set"}</strong>
                        </div>
                        <div>
                            <span>Closes</span>
                            <strong>{pendingCreate.payload.expiresAt || "Not set"}</strong>
                        </div>
                        <div>
                            <span>Token</span>
                            <strong>{pendingCreate.payload.token || "Not set"}</strong>
                        </div>
                    </div>

                    {isDigitalAttendanceKind(pendingCreate.payload.kind) ? (
                        <div className="survey-confirm-email-grid">
                            <section>
                                <h4>Attendance register</h4>
                                <p>The link and token can be placed on an agenda or beside the physical register. Each submission also writes to the linked meeting attendance record.</p>
                            </section>
                            <section>
                                <h4>Invite email sent now</h4>
                                {pendingCreate.inviteEmails.length ? (
                                    <ul>
                                        {pendingCreate.inviteEmails.map((email) => <li key={`invite-${email}`}>{email}</li>)}
                                    </ul>
                                ) : (
                                    <p>No invite recipients entered. The register will be created, but no invite email will be sent yet.</p>
                                )}
                            </section>
                            <section>
                                <h4>Close-summary email sent later</h4>
                                {pendingCreate.summaryEmails.length ? (
                                    <ul>
                                        {pendingCreate.summaryEmails.map((email) => <li key={`summary-${email}`}>{email}</li>)}
                                    </ul>
                                ) : (
                                    <p>No manual summary recipients entered.</p>
                                )}
                                <p>The app will also try the lodge Master and Secretary from officer records when the register closes.</p>
                            </section>
                        </div>
                    ) : (
                        <div className="survey-confirm-email-grid">
                            <section>
                                <h4>Invite email sent now</h4>
                                {pendingCreate.inviteEmails.length ? (
                                    <ul>
                                        {pendingCreate.inviteEmails.map((email) => <li key={`invite-${email}`}>{email}</li>)}
                                    </ul>
                                ) : (
                                    <p>No invite recipients entered. The survey will be created, but no invite email will be sent yet.</p>
                                )}
                            </section>
                            <section>
                                <h4>Close-summary email sent later</h4>
                                {pendingCreate.summaryEmails.length ? (
                                    <ul>
                                        {pendingCreate.summaryEmails.map((email) => <li key={`summary-${email}`}>{email}</li>)}
                                    </ul>
                                ) : (
                                    <p>No manual summary recipients entered.</p>
                                )}
                                <p>The app will also try the lodge Master and Secretary from officer records when the RSVP closes.</p>
                            </section>
                        </div>
                    )}

                    <div className="survey-confirm-actions">
                        <button type="button" className="secondary" onClick={() => setPendingCreate(null)}>
                            Cancel
                        </button>
                        <button type="button" className="secondary" onClick={() => setPendingCreate(null)}>
                            Add More Emails
                        </button>
                        <button type="button" onClick={confirmQuickAttendanceSurvey}>
                            {isDigitalAttendanceKind(pendingCreate.payload.kind) ? "Create Register" : "Create RSVP"}
                        </button>
                    </div>
                </div>
            </div>
        )}
        </>
    );
}

export default SurveyPage;
